/**
 * Dettaglio preventivo con righe, info fornitore, invio email,
 * aggiunta articoli dal catalogo, inserimento prezzo inline,
 * upload PDF, approvazione e rifiuto.
 *
 * Flusso:
 * 1. Operatore crea preventivo con articoli e quantita
 * 2. Puo inviare la richiesta al fornitore via email
 * 3. Fornitore risponde con i prezzi
 * 4. Operatore inserisce i prezzi nelle righe (click inline)
 * 5. Titolare approva → genera ordine con solo righe confermate
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BookmarkPlus, GripVertical, Pencil } from 'lucide-react';
import { Button, Card, Combobox, type ComboboxOption, Input } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import { useAuth } from '@shell/AuthContext';
import { SoggettoQuickCreateDialog, type SoggettoCreato } from '../components/SoggettoQuickCreateDialog';
import { DuplicaPreventivoDialog } from '../components/DuplicaPreventivoDialog';
import { CambiaIntestatarioDialog } from '../components/CambiaIntestatarioDialog';

interface RigaPreventivo {
  id: string;
  preventivo_id: string;
  articolo_id: string | null;
  // Flag servizio dell'articolo collegato (catalogo unico articoli/servizi,
  // DEC-013): risolto solo nel dettaglio preventivo, null se la riga non e'
  // collegata a un articolo o l'articolo non risolve piu'.
  articolo_is_servizio: boolean | null;
  codice_articolo: string | null;
  descrizione: string;
  unita_misura: string | null;
  quantita: string;
  prezzo_unitario: string;
  iva_perc: string | null;
  sconto_perc: string | null;
  importo_riga: string;
  // Costo/distributore + margine derivato (DEC-083)
  distributore_id: string | null;
  costo_unitario: string | null;
  costo_origine: string | null;
  catalogo_fornitore_id: string | null;
  margine_perc: string | null;
  guadagno_netto: string | null;
  sotto_soglia: boolean;
  scostamento_costo: string | null;
  confermata: boolean;
  note: string | null;
  // Ordine di visualizzazione righe (Task riordino): usato per il drag
  // nativo e il pulsante "Ordina per codice" nel dettaglio.
  ordine?: number | null;
  // Etichetta di raggruppamento per i subtotali nel PDF (Feature E). Nullable,
  // stringa vuota nel PUT righe la azzera.
  sezione?: string | null;
  // Tipo riga persistito: false = Prodotto, true = Servizio. Per righe da
  // catalogo e' lo snapshot del flag articolo (non editabile); per righe libere
  // e' editabile dal dropdown.
  is_servizio: boolean;
}

interface Preventivo {
  id: string;
  tenant_id: string;
  tipo: 'acquisto' | 'vendita';
  tipo_offerta: 'a_corpo' | 'dettagliato' | 'misto';
  oggetto: string | null;
  premessa: string | null;
  condizioni_fornitura: string | null;
  importo_a_corpo: string | null;
  riferimento_progetto: string | null;
  fornitore_id: string | null;
  cliente_id: string | null;
  rappresentante_id: string | null;
  richiesta_id: string | null;
  numero: string;
  anno: number;
  stato: string;
  scaduto: boolean;
  unita_organizzativa_id: string | null;
  soglia_margine_perc: string | null;
  margine_totale_perc: string | null;
  guadagno_netto_totale: string | null;
  righe_sotto_soglia: number;
  data_preventivo: string;
  data_validita: string | null;
  valuta: string;
  importo_totale: string;
  termini_pagamento: string | null;
  modalita_pagamento: string | null;
  sconto_globale_perc: string | null;
  spese_spedizione: string | null;
  minimo_ordine: string | null;
  tempo_consegna_giorni: number | null;
  file_pdf_path: string | null;
  note: string | null;
  righe: RigaPreventivo[];
  fornitore_nome: string | null;
  fornitore_email: string | null;
  fornitore_pec: string | null;
  fornitore_telefono: string | null;
  fornitore_partita_iva: string | null;
  cliente_nome: string | null;
  cliente_email: string | null;
  cliente_pec: string | null;
  cliente_telefono: string | null;
  cliente_partita_iva: string | null;
}

interface CatalogoItem {
  id: string;
  sku: string;
  descrizione: string | null;
  moq: string;
}

// Risposta GET /preventivi/{id}/anteprima-email (T5): oggetto/corpo
// renderizzati dal modello configurato (o default hardcoded) usati per
// precompilare i dialog di invio, editabili prima dell'invio reale.
interface AnteprimaEmail {
  oggetto: string;
  corpo: string;
  destinatari_default: string[];
}

// Opzione di una lista enumerata configurabile (modalita' pagamento, unita di
// misura): il documento conserva sempre la descrizione come testo libero (no
// FK), coerente col pattern gia' usato in NuovoPreventivoVendita.tsx e nel
// backend (core/app/api/v1/unita_misura.py, modalita_pagamento.py).
interface LookupOpt {
  id: string;
  codice: string;
  descrizione: string;
}

const STATI_BADGE: Record<string, { label: string; className: string }> = {
  bozza: { label: 'Bozza', className: 'bg-muted text-muted-foreground' },
  in_revisione: { label: 'In revisione', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  inviato: { label: 'Inviato', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  approvato: { label: 'Approvato', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  ordine_generato: { label: 'Ordine generato', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  rifiutato: { label: 'Rifiutato', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
};

// --- Calcoli live (Fix A) ---------------------------------------------------
// Replicano lato client, con arrotondamento a 2 decimali, le stesse formule del
// backend (vedi ordini/backend/services.py: _importo_riga_netto,
// calcola_margine_riga, _ricalcola_totale_preventivo) cosi' che riga e totali
// reagiscano mentre l'utente digita, senza attendere il salvataggio + reload.

/**
 * Arrotonda a 2 decimali (equivalente di Decimal.quantize(0.01)).
 * Nota: JS Math.round usa round-half-up, mentre Python Decimal usa ROUND_HALF_EVEN;
 * puo' esserci uno scarto di 1 cent transitorio solo in anteprima, il valore
 * autoritativo arriva dal backend dopo il salvataggio.
 */
function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/**
 * Imponibile di riga = quantita * prezzo * (1 - sconto%/100), arrotondato a 0.01.
 * Input non numerici/vuoti sono trattati come 0 (mai NaN in output).
 */
function calcolaImportoRiga(
  quantitaRaw: string | null | undefined,
  prezzoRaw: string | null | undefined,
  scontoRaw: string | null | undefined,
): number {
  const q = parseFloat(quantitaRaw ?? '');
  const p = parseFloat(prezzoRaw ?? '');
  const s = parseFloat(scontoRaw ?? '');
  const qq = Number.isFinite(q) ? q : 0;
  const pp = Number.isFinite(p) ? p : 0;
  const ss = Number.isFinite(s) ? s : 0;
  return round2(qq * pp * (1 - ss / 100));
}

/**
 * Margine di riga (netto IVA), sul modello di calcola_margine_riga:
 * costo_totale = costo_unitario * quantita; guadagno = importo_riga - costo_totale;
 * margine% = guadagno / importo_riga * 100 (solo se importo_riga > 0).
 * Ritorna null se il costo non e' disponibile (righe servizio / senza distributore).
 */
function calcolaMargineRiga(
  importoRiga: number,
  quantitaRaw: string | null | undefined,
  costoUnitarioRaw: string | null | undefined,
): { guadagno: number | null; marginePerc: number | null } {
  if (costoUnitarioRaw == null || costoUnitarioRaw === '') {
    return { guadagno: null, marginePerc: null };
  }
  const costo = parseFloat(costoUnitarioRaw);
  if (!Number.isFinite(costo)) {
    return { guadagno: null, marginePerc: null };
  }
  const q = parseFloat(quantitaRaw ?? '');
  const qq = Number.isFinite(q) ? q : 0;
  const costoTotale = round2(costo * qq);
  const guadagno = round2(importoRiga - costoTotale);
  const marginePerc = importoRiga > 0 ? round2((guadagno / importoRiga) * 100) : null;
  return { guadagno, marginePerc };
}

/**
 * Confronto naturale per codici articolo alfanumerici (Task riordino):
 * spezza la stringa in token numerici/non-numerici e confronta i token
 * numerici come interi, cosi' A2 precede A10 (non ordinamento lessicografico
 * puro, dove "A10" < "A2").
 */
function confrontaCodiceNaturale(a: string | null, b: string | null): number {
  const sa = a ?? '', sb = b ?? '';
  const toks = (s: string) => s.match(/\d+|\D+/g) ?? [];
  const ta = toks(sa), tb = toks(sb);
  for (let i = 0; i < Math.max(ta.length, tb.length); i++) {
    const x = ta[i] ?? '', y = tb[i] ?? '';
    const nx = /^\d+$/.test(x), ny = /^\d+$/.test(y);
    if (nx && ny) { const d = parseInt(x, 10) - parseInt(y, 10); if (d) return d; }
    else if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

export function PreventivoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currency, date, qty } = useFormatters();
  const { hasPermesso } = useAuth();
  const token = localStorage.getItem('token');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [preventivo, setPreventivo] = useState<Preventivo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Catalogo fornitore per aggiungere righe
  const [catalogo, setCatalogo] = useState<CatalogoItem[]>([]);
  const [catalogoSearch, setCatalogoSearch] = useState('');
  const [showCatalogo, setShowCatalogo] = useState(false);

  // Quantita per aggiunta da catalogo
  const [catalogoQty, setCatalogoQty] = useState<Record<string, string>>({});

  // Lookup configurabili (Impostazioni Azienda): modalita' di pagamento e
  // unita di misura. Caricate al mount in parallelo con fetchData (Task D+F)
  // e ricaricate all'apertura del picker modalita' (voci configurate in
  // /admin/azienda mentre questa pagina e' gia' aperta).
  const [modalitaPagamentoOpts, setModalitaPagamentoOpts] = useState<LookupOpt[]>([]);
  const [unitaMisuraOpts, setUnitaMisuraOpts] = useState<LookupOpt[]>([]);
  // Edit inline della modalita' di pagamento in intestazione.
  // modalitaSearch viene precompilata col valore corrente per contesto, ma
  // filtra la lista SOLO dopo che l'utente digita (modalitaDirty): altrimenti
  // il picker mostrerebbe solo la voce corrente (o nessuna, se il testo
  // salvato non corrisponde piu' a una voce configurata).
  const [editingModalita, setEditingModalita] = useState(false);
  const [modalitaSearch, setModalitaSearch] = useState('');
  const [modalitaDirty, setModalitaDirty] = useState(false);

  // Inline edit generico per campi numerici (prezzo, iva_perc, sconto_perc, costo) + descrizione/UM/codice (testo)
  const [editingField, setEditingField] = useState<{ rigaId: string; campo: 'quantita' | 'prezzo_unitario' | 'iva_perc' | 'sconto_perc' | 'costo_unitario' | 'descrizione' | 'unita_misura' | 'codice_articolo' } | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // Inline edit sezione riga (Feature E, Task 11): separato dal blocco sopra
  // perche' il comportamento di salvataggio e' diverso (stringa vuota azzera
  // invece di essere ignorata come descrizione/UM/codice) e non serve nei
  // totali live (getEffectiveField).
  const [editingSezioneRigaId, setEditingSezioneRigaId] = useState<string | null>(null);
  const [editingSezioneValue, setEditingSezioneValue] = useState('');

  // Rinomina gruppo/sezione dalla banda di intestazione (rifinitura Walter,
  // 2026-07-20): il vecchio bottoncino per-riga sotto la descrizione resta
  // per assegnare la sezione a righe senza gruppo, ma per rinominare un
  // gruppo GIA' esistente e' scomodo doverlo fare riga per riga. Qui si
  // rinomina l'intero gruppo in un colpo solo (PUT su tutte le righe del
  // gruppo cliccato, una sola fetchData alla fine).
  const [editingGruppoId, setEditingGruppoId] = useState<string | null>(null);
  const [editingGruppoValue, setEditingGruppoValue] = useState('');

  // Aggiunta righe per preventivo vendita (prodotto da magazzino oppure servizio libero)
  const [addMode, setAddMode] = useState<'prodotto' | 'servizio' | null>(null);
  const [articoliQuery, setArticoliQuery] = useState('');
  interface ArticoloMagazzino {
    id: string;
    sku_interno: string;
    descrizione: string;
    unita_misura: string | null;
    is_servizio: boolean;
  }
  const [articoliResults, setArticoliResults] = useState<ArticoloMagazzino[]>([]);
  const [articoliLoading, setArticoliLoading] = useState(false);
  const [articoloSelezionato, setArticoloSelezionato] = useState<ArticoloMagazzino | null>(null);
  // Distributore per riga (fonte del costo, DEC-083): soggetti tipo='fornitore'
  interface DistributoreOpt {
    id: string;
    ragione_sociale: string | null;
    nome: string | null;
    cognome: string | null;
  }
  const [distributoriQuery, setDistributoriQuery] = useState('');
  const [distributoriResults, setDistributoriResults] = useState<DistributoreOpt[]>([]);
  const [distributoriLoading, setDistributoriLoading] = useState(false);
  const [distributoreSelezionato, setDistributoreSelezionato] = useState<DistributoreOpt | null>(null);
  // Creazione rapida fornitore dai 3 picker di questa pagina (distributore,
  // fornitore preferenziale nuovo articolo, fornitore preferenziale riga
  // esistente): un solo dialog condiviso, target = quale stato selezionare.
  const [quickCreateTarget, setQuickCreateTarget] = useState<
    'distributore' | 'catalogoFornitore' | 'rigaCatalogoFornitore' | null
  >(null);
  // Dialog "Duplica per altro cliente/fornitore" (Task 7): separato da
  // riformula, disponibile su qualsiasi stato del preventivo.
  const [duplicaOpen, setDuplicaOpen] = useState(false);
  // Dialog "Cambia cliente/fornitore" (cantiere elimina-bozza/cambio-
  // intestatario): a differenza di Duplica, questo modifica il documento
  // ESISTENTE al posto di crearne una copia, ed e' consentito SOLO in
  // bozza (v. CambiaIntestatarioDialog.tsx).
  const [cambiaIntestatarioOpen, setCambiaIntestatarioOpen] = useState(false);
  // Errore di eliminazione (bozza gia' cambiata di stato da un'altra
  // sessione, o preventivo ancora collegato a una richiesta fornitore):
  // il messaggio del backend va mostrato, non ingoiato (v. handleElimina).
  const [eliminaErrore, setEliminaErrore] = useState<string | null>(null);
  // Dialog "Genera ordine di vendita": file dell'ordine firmato dal cliente
  // OPZIONALE (si puo' allegare ora o dopo dal dettaglio ordine).
  const [generaOrdineOpen, setGeneraOrdineOpen] = useState(false);
  const [fileFirmato, setFileFirmato] = useState<File | null>(null);
  // Campi comuni per aggiunta (sia prodotto sia servizio)
  const [nuovaDescrizione, setNuovaDescrizione] = useState('');
  const [nuovaUM, setNuovaUM] = useState('');
  const [nuovaQuantita, setNuovaQuantita] = useState('');
  const [nuovoPrezzo, setNuovoPrezzo] = useState('');
  const [nuovaIva, setNuovaIva] = useState('22');
  const [nuovoSconto, setNuovoSconto] = useState('');
  const [nuovoCosto, setNuovoCosto] = useState('');
  const [nuovoCostoOrigine, setNuovoCostoOrigine] = useState<string | null>(null);
  const [nuovoCatalogoFornitoreId, setNuovoCatalogoFornitoreId] = useState<string | null>(null);
  // Per a_corpo: descrizione lunga (va in `note` della riga)
  const [nuovaNoteACorpo, setNuovaNoteACorpo] = useState('');

  // Salva voce libera (servizio) nel catalogo articoli/servizi, cosi' da
  // poterla riusare in futuro senza ridigitarla (Task catalogo-voci-preventivo).
  // Richiede magazzino.modifica (gate visibilita' checkbox) e, per lo schema
  // ArticoloCreate, un fornitore_preferenziale_id obbligatorio (DEC-047) anche
  // per un servizio: non esiste un fornitore "generico" di default, va scelto.
  const [salvaCatalogo, setSalvaCatalogo] = useState(false);
  const [nuovoCodiceCatalogo, setNuovoCodiceCatalogo] = useState('');
  const [catalogoFornitoreQuery, setCatalogoFornitoreQuery] = useState('');
  const [catalogoFornitoreResults, setCatalogoFornitoreResults] = useState<DistributoreOpt[]>([]);
  const [catalogoFornitoreLoading, setCatalogoFornitoreLoading] = useState(false);
  const [catalogoFornitoreSelezionato, setCatalogoFornitoreSelezionato] = useState<DistributoreOpt | null>(null);
  const [catalogoSaveError, setCatalogoSaveError] = useState<string | null>(null);
  // Articolo creato a catalogo con successo ma la cui riga non e' ancora
  // stata aggiunta (POST riga fallita dopo). Persistito in stato (non solo
  // variabile locale della funzione) cosi' un retry riusa id/sku invece di
  // ricreare l'articolo: unique parziale idx_am_sku (tenant_id, sku_interno)
  // WHERE deleted_at IS NULL presente lato server (modules/magazzino/
  // migrations/versions/0001_initial.py); la guardia client qui evita il
  // roundtrip e il 409 che il server tornerebbe su un duplicato reale.
  const [catalogoArticoloCreato, setCatalogoArticoloCreato] = useState<{ id: string; sku: string } | null>(null);

  // Salva a catalogo per una riga GIA' ESISTENTE del preventivo (Task
  // catalogo-voci-preventivo T3): una voce libera scritta a mano (es.
  // "Abbonamento Supremo Control", codice ABB-SUPREMO, articolo_id nullo)
  // deve poter essere agganciata a un nuovo articolo di magazzino senza
  // dover cancellare e ricreare la riga. Un solo pannello aperto alla volta
  // (rigaCatalogoPanelId identifica la riga); i campi del form (codice,
  // fornitore, is_servizio, errore) si azzerano su Annulla/successo/cambio
  // preventivo, ma rigaCatalogoArticoloCreato (sotto) e' tenuto per-riga e
  // NON viene azzerato su Annulla: un articolo gia' creato a catalogo per
  // quella riga resta riusabile alla riapertura (vedi review 94af5f7,
  // Finding 1 - altrimenti la riapertura ri-POSTava lo stesso SKU e finiva
  // sul 409/idx_am_sku).
  const [rigaCatalogoPanelId, setRigaCatalogoPanelId] = useState<string | null>(null);
  const [rigaCatalogoCodice, setRigaCatalogoCodice] = useState('');
  const [rigaCatalogoIsServizio, setRigaCatalogoIsServizio] = useState(true);
  const [rigaCatalogoFornitoreQuery, setRigaCatalogoFornitoreQuery] = useState('');
  const [rigaCatalogoFornitoreResults, setRigaCatalogoFornitoreResults] = useState<DistributoreOpt[]>([]);
  const [rigaCatalogoFornitoreLoading, setRigaCatalogoFornitoreLoading] = useState(false);
  const [rigaCatalogoFornitoreSelezionato, setRigaCatalogoFornitoreSelezionato] = useState<DistributoreOpt | null>(null);
  const [rigaCatalogoSaveError, setRigaCatalogoSaveError] = useState<string | null>(null);
  const [rigaCatalogoActionLoading, setRigaCatalogoActionLoading] = useState(false);
  // Articolo creato con successo ma la cui riga non e' ancora stata
  // aggiornata (PUT fallita dopo): persistito PER RIGA (chiave = rigaId, non
  // "il pannello aperto") cosi' un retry riusa id/sku invece di ricreare un
  // secondo articolo con lo stesso SKU. Unique parziale idx_am_sku
  // (tenant_id, sku_interno) WHERE deleted_at IS NULL presente lato server
  // (modules/magazzino/migrations/versions/0001_initial.py): la guardia qui
  // evita il roundtrip e il 409 che il server tornerebbe sul duplicato,
  // stesso pattern di catalogoArticoloCreato. Chiave per riga (non un singolo
  // stato "del pannello") perche' l'utente puo' passare a un'altra riga
  // mentre una richiesta e' in volo (vedi rigaCatalogoPanelIdRef).
  const [rigaCatalogoArticoloCreato, setRigaCatalogoArticoloCreato] = useState<Record<string, { id: string; sku: string }>>({});
  // Specchio sempre aggiornato di rigaCatalogoPanelId, letto DENTRO
  // handleSalvaRigaACatalogo dopo un await: la chiusura della funzione
  // cattura il valore di rigaCatalogoPanelId al momento della chiamata, che
  // diventa stale se nel frattempo l'utente ha chiuso il pannello o ne ha
  // aperto un altro. Confrontando col ref al momento della risposta si evita
  // che una richiesta in volo per la riga A sporchi lo stato del pannello
  // nel frattempo aperto sulla riga B (Finding 1, review 94af5f7).
  const rigaCatalogoPanelIdRef = useRef<string | null>(null);
  useEffect(() => {
    rigaCatalogoPanelIdRef.current = rigaCatalogoPanelId;
  }, [rigaCatalogoPanelId]);

  // Editing intestazione contrattuale (a_corpo / misto)
  const [showEditIntestazione, setShowEditIntestazione] = useState(false);
  const [editOggetto, setEditOggetto] = useState('');
  const [editPremessa, setEditPremessa] = useState('');
  const [editCondizioni, setEditCondizioni] = useState('');
  const [editRiferimento, setEditRiferimento] = useState('');
  const [editImportoACorpo, setEditImportoACorpo] = useState('');
  const [editTipoOfferta, setEditTipoOfferta] = useState<'a_corpo' | 'dettagliato' | 'misto'>('dettagliato');
  const [editNote, setEditNote] = useState('');

  // Invio email (acquisto - fornitore)
  const [showInvio, setShowInvio] = useState(false);
  const [invioEmail, setInvioEmail] = useState('');
  const [invioOggetto, setInvioOggetto] = useState('');
  const [invioCorpo, setInvioCorpo] = useState('');
  const [invioResult, setInvioResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Invio preventivo vendita (al cliente, dialog)
  const [showInviaVendita, setShowInviaVendita] = useState(false);
  const [invioVDestType, setInvioVDestType] = useState<'cliente' | 'altro'>('cliente');
  const [invioVCanale, setInvioVCanale] = useState<'email' | 'whatsapp' | 'telegram'>('email');
  const [invioVEmail, setInvioVEmail] = useState('');
  const [invioVCC, setInvioVCC] = useState('');
  const [invioVCCN, setInvioVCCN] = useState('');
  const [invioVOggetto, setInvioVOggetto] = useState('');
  const [invioVCorpo, setInvioVCorpo] = useState('');
  const [invioVResult, setInvioVResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [allegatiInvio, setAllegatiInvio] = useState<File[]>([]);

  // Anteprima email modificabile (T5): condivisa tra i due dialog di invio.
  // Non sono mai aperti insieme (dipendono da preventivo.tipo, fisso per
  // record: 'vendita' abilita showInviaVendita, 'acquisto' abilita showInvio).
  const [anteprimaLoading, setAnteprimaLoading] = useState(false);
  const [anteprimaError, setAnteprimaError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/preventivi/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPreventivo(data);
        setLoadError(null);
        // Pre-fill email invio con PEC o email fornitore
        if (!invioEmail) {
          setInvioEmail(data.fornitore_pec || data.fornitore_email || '');
        }
      } else if (res.status === 404) {
        setLoadError('Preventivo non trovato');
      } else {
        setLoadError(`Errore ${res.status} nel caricamento del preventivo`);
      }
    } catch {
      setLoadError('Errore di rete nel caricamento del preventivo');
    }
    setLoading(false);
  }, [id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Carica i lookup configurabili (modalita' pagamento, unita di misura) al
  // mount, in parallelo col caricamento del preventivo, e di nuovo quando si
  // apre il picker della modalita' (freschezza rispetto a /admin/azienda).
  // Liste enumerate piccole: nessun bisogno di ricerca server-side, il filtro
  // sul testo digitato e' locale (vedi Combobox in Riepilogo e nel form riga).
  const fetchLookups = useCallback(async () => {
    const [mRes, uRes] = await Promise.all([
      fetch('/api/v1/config/modalita-pagamento?solo_attive=true', {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch('/api/v1/config/unita-misura?solo_attive=true', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (mRes.ok) {
      const data = await mRes.json();
      setModalitaPagamentoOpts(Array.isArray(data) ? data : []);
    }
    if (uRes.ok) {
      const data = await uRes.json();
      setUnitaMisuraOpts(Array.isArray(data) ? data : []);
    }
  }, [token]);

  useEffect(() => {
    void fetchLookups();
  }, [fetchLookups]);

  // Carica catalogo fornitore
  useEffect(() => {
    if (!preventivo?.fornitore_id || !showCatalogo) return;
    const fetchCatalogo = async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (catalogoSearch) params.set('q', catalogoSearch);
      const res = await fetch(
        `/api/v1/fornitori/${preventivo.fornitore_id}/catalogo?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) setCatalogo(await res.json());
    };
    fetchCatalogo();
  }, [preventivo?.fornitore_id, catalogoSearch, showCatalogo, token]);

  const handleUploadPdf = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setActionLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`/api/v1/preventivi/${id}/upload-pdf`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (res.ok) await fetchData();
    setActionLoading(false);
  };

  const handleAddFromCatalogo = async (item: CatalogoItem) => {
    const qtyVal = catalogoQty[item.id] || '';
    if (!qtyVal || parseFloat(qtyVal) <= 0) return;
    setActionLoading(true);
    await fetch(`/api/v1/preventivi/${id}/righe`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        descrizione: item.descrizione || item.sku,
        quantita: qtyVal,
        codice_articolo: item.sku,
        confermata: true,
      }),
    });
    await fetchData();
    setActionLoading(false);
  };

  const handleToggleRiga = async (rigaId: string, confermata: boolean) => {
    await fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ confermata: !confermata }),
    });
    await fetchData();
  };

  // Tipo riga Prodotto/Servizio (righe libere): PUT diretto del flag persistito.
  // Le righe da catalogo non passano di qui (badge read-only dal flag articolo).
  const handleSaveIsServizio = async (rigaId: string, value: boolean) => {
    await fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_servizio: value }),
    });
    await fetchData();
  };

  const handleDeleteRiga = async (rigaId: string) => {
    await fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchData();
  };

  // Sezione riga (Feature E, Task 11): raggruppamento per i subtotali nel
  // PDF. Stringa vuota azzera la sezione (backend: 'sezione' in
  // model_fields_set -> riga.sezione = data.sezione or None).
  const handleSaveSezione = async (rigaId: string, value: string) => {
    await fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sezione: value.trim() }),
    });
    setEditingSezioneRigaId(null);
    setEditingSezioneValue('');
    await fetchData();
  };

  // Rinomina di gruppo (banda di intestazione, Walter 2026-07-20): applica il
  // nuovo nome a TUTTE le righe di quel gruppo (stessa API della sezione
  // per-riga sopra). Stringa vuota azzera la sezione su tutte -> scioglie il
  // gruppo. Una sola fetchData alla fine per evitare N reload.
  const handleRenameGruppo = async (rigaIds: string[], value: string) => {
    const nuovoValore = value.trim();
    await Promise.all(
      rigaIds.map((rigaId) =>
        fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sezione: nuovoValore }),
        }),
      ),
    );
    setEditingGruppoId(null);
    setEditingGruppoValue('');
    await fetchData();
  };

  // Riordino righe (Task riordino): salva il nuovo ordine (array di id in
  // sequenza) e ricarica il dettaglio - l'endpoint ritorna solo la testata,
  // le righe aggiornate arrivano col GET di fetchData.
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const salvaOrdine = async (rigaIds: string[]) => {
    const res = await fetch(`/api/v1/preventivi/${id}/righe/riordina`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ riga_ids: rigaIds }),
    });
    if (res.ok) await fetchData();
  };

  const ordinaPerCodice = async () => {
    if (!preventivo) return;
    const ordinate = [...preventivo.righe]
      .sort((a, b) => confrontaCodiceNaturale(a.codice_articolo ?? null, b.codice_articolo ?? null))
      .map((r) => r.id);
    await salvaOrdine(ordinate);
  };

  // overrideValue: usato dal picker UM (Combobox) per salvare subito il
  // valore selezionato senza aspettare il re-render di editingValue (evita
  // una race di closure stale tra onSelect e lo stato React).
  const handleSaveField = async (overrideValue?: string) => {
    if (!editingField) return;
    const currentEditingValue = overrideValue ?? editingValue;
    // descrizione e' testo libero (NOT NULL lato backend): niente parseFloat,
    // stringa vuota/solo spazi non si salva (si mantiene il valore precedente).
    if (editingField.campo === 'descrizione') {
      const trimmed = currentEditingValue.trim();
      if (!trimmed) {
        setEditingField(null);
        setEditingValue('');
        return;
      }
      await fetch(`/api/v1/preventivi/${id}/righe/${editingField.rigaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ descrizione: trimmed }),
      });
      setEditingField(null);
      setEditingValue('');
      await fetchData();
      return;
    }
    // unita_misura e' testo libero come descrizione, ma nullable lato DB
    // (RigaPreventivo.unita_misura String(50)). Il backend pero' applica il
    // campo solo con `if data.unita_misura is not None` (preventivi.py,
    // modifica_riga ~1034): un null esplicito nel body arriva a Pydantic
    // indistinguibile dal campo omesso (nessun exclude_unset), quindi NON
    // esiste oggi un modo per azzerare l'UM via questo endpoint. UM vuota =
    // non si salva nulla, si mantiene il valore precedente.
    if (editingField.campo === 'unita_misura') {
      const trimmed = currentEditingValue.trim();
      if (!trimmed) {
        setEditingField(null);
        setEditingValue('');
        return;
      }
      await fetch(`/api/v1/preventivi/${id}/righe/${editingField.rigaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ unita_misura: trimmed }),
      });
      setEditingField(null);
      setEditingValue('');
      await fetchData();
      return;
    }
    // codice_articolo e' testo libero (nullable lato DB, RigaPreventivo
    // String(100)) col medesimo pattern skip-save di unita_misura: il
    // backend applica il campo solo con `if data.codice_articolo is not
    // None` (preventivi.py, modifica_riga ~1055), quindi un vuoto non
    // azzera il valore precedente, semplicemente non si invia nulla.
    if (editingField.campo === 'codice_articolo') {
      const trimmed = currentEditingValue.trim();
      if (!trimmed) {
        setEditingField(null);
        setEditingValue('');
        return;
      }
      await fetch(`/api/v1/preventivi/${id}/righe/${editingField.rigaId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ codice_articolo: trimmed }),
      });
      setEditingField(null);
      setEditingValue('');
      await fetchData();
      return;
    }
    if (currentEditingValue === '') return;
    const body: Record<string, string | null> = {};
    body[editingField.campo] = currentEditingValue;
    // Un costo modificato a mano diventa un override manuale.
    if (editingField.campo === 'costo_unitario') body.costo_origine = 'manuale';
    await fetch(`/api/v1/preventivi/${id}/righe/${editingField.rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    setEditingField(null);
    setEditingValue('');
    await fetchData();
  };

  const startEdit = (rigaId: string, campo: 'quantita' | 'prezzo_unitario' | 'iva_perc' | 'sconto_perc' | 'costo_unitario' | 'descrizione' | 'unita_misura' | 'codice_articolo', currentValue: string | null | undefined) => {
    setEditingField({ rigaId, campo });
    setEditingValue(currentValue ?? '');
  };

  // Ricerca articoli magazzino debounced (per preventivo vendita)
  useEffect(() => {
    if (preventivo?.tipo !== 'vendita' || addMode !== 'prodotto') return;
    if (articoloSelezionato) return; // gia' selezionato, non ricerca
    const timer = setTimeout(async () => {
      if (!articoliQuery.trim()) {
        setArticoliResults([]);
        return;
      }
      setArticoliLoading(true);
      try {
        const params = new URLSearchParams({ q: articoliQuery.trim(), limit: '20' });
        const res = await fetch(`/api/v1/magazzino/articoli?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setArticoliResults(Array.isArray(data) ? data : data.items ?? []);
        }
      } finally {
        setArticoliLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [articoliQuery, preventivo?.tipo, addMode, articoloSelezionato, token]);

  // Ricerca distributori (soggetti fornitore) per la riga
  useEffect(() => {
    if (preventivo?.tipo !== 'vendita' || addMode !== 'prodotto') return;
    if (distributoreSelezionato) return;
    const timer = setTimeout(async () => {
      if (!distributoriQuery.trim()) {
        setDistributoriResults([]);
        return;
      }
      setDistributoriLoading(true);
      try {
        const params = new URLSearchParams({ tipo: 'fornitore', q: distributoriQuery.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDistributoriResults(Array.isArray(data) ? data : data.items ?? []);
        }
      } finally {
        setDistributoriLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [distributoriQuery, preventivo?.tipo, addMode, distributoreSelezionato, token]);

  // Ricerca fornitori per il "Salva nel catalogo" (fornitore_preferenziale_id
  // obbligatorio su ArticoloCreate, DEC-047). Stesso endpoint dei distributori.
  useEffect(() => {
    if (addMode !== 'servizio' || !salvaCatalogo) return;
    if (catalogoFornitoreSelezionato) return;
    const timer = setTimeout(async () => {
      if (!catalogoFornitoreQuery.trim()) {
        setCatalogoFornitoreResults([]);
        return;
      }
      setCatalogoFornitoreLoading(true);
      try {
        const params = new URLSearchParams({ tipo: 'fornitore', q: catalogoFornitoreQuery.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setCatalogoFornitoreResults(Array.isArray(data) ? data : data.items ?? []);
        }
      } finally {
        setCatalogoFornitoreLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [catalogoFornitoreQuery, addMode, salvaCatalogo, catalogoFornitoreSelezionato, token]);

  // Ricerca fornitori per il pannello "Salva a catalogo" di una riga
  // esistente (stesso endpoint/pattern del form di aggiunta sopra).
  useEffect(() => {
    if (!rigaCatalogoPanelId) return;
    if (rigaCatalogoFornitoreSelezionato) return;
    const timer = setTimeout(async () => {
      if (!rigaCatalogoFornitoreQuery.trim()) {
        setRigaCatalogoFornitoreResults([]);
        return;
      }
      setRigaCatalogoFornitoreLoading(true);
      try {
        const params = new URLSearchParams({ tipo: 'fornitore', q: rigaCatalogoFornitoreQuery.trim(), limit: '20' });
        const res = await fetch(`/api/v1/soggetti?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setRigaCatalogoFornitoreResults(Array.isArray(data) ? data : data.items ?? []);
        }
      } finally {
        setRigaCatalogoFornitoreLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [rigaCatalogoFornitoreQuery, rigaCatalogoPanelId, rigaCatalogoFornitoreSelezionato, token]);

  /**
   * Fornitore creato al volo da uno dei 3 picker fornitore di questa pagina
   * (distributore, fornitore preferenziale nuovo articolo, fornitore
   * preferenziale riga esistente): lo inserisce nella lista risultati del
   * picker che ha aperto il dialog e lo seleziona subito.
   */
  const handleFornitoreQuickCreato = useCallback(
    (creato: SoggettoCreato) => {
      const opt: DistributoreOpt = {
        id: creato.id,
        ragione_sociale: creato.ragione_sociale,
        nome: creato.nome,
        cognome: creato.cognome,
      };
      if (quickCreateTarget === 'distributore') {
        setDistributoriResults((prev) => [opt, ...prev]);
        setDistributoreSelezionato(opt);
      } else if (quickCreateTarget === 'catalogoFornitore') {
        setCatalogoFornitoreResults((prev) => [opt, ...prev]);
        setCatalogoFornitoreSelezionato(opt);
      } else if (quickCreateTarget === 'rigaCatalogoFornitore') {
        setRigaCatalogoFornitoreResults((prev) => [opt, ...prev]);
        setRigaCatalogoFornitoreSelezionato(opt);
      }
      setQuickCreateTarget(null);
    },
    [quickCreateTarget],
  );

  // Auto-fill prezzo (listino ramo) e costo (catalogo distributore) alla scelta
  // di articolo/distributore. Il prezzo si precompila solo se ancora vuoto.
  useEffect(() => {
    if (preventivo?.tipo !== 'vendita' || addMode !== 'prodotto') return;
    if (!articoloSelezionato) return;
    const run = async () => {
      const params = new URLSearchParams();
      params.set('articolo_id', articoloSelezionato.id);
      if (preventivo?.unita_organizzativa_id) params.set('unita_organizzativa_id', preventivo.unita_organizzativa_id);
      if (distributoreSelezionato) params.set('distributore_id', distributoreSelezionato.id);
      if (articoloSelezionato.sku_interno) params.set('sku', articoloSelezionato.sku_interno);
      const res = await fetch(`/api/v1/preventivi/lookup-riga?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const d = await res.json();
      if (d.prezzo_consigliato != null && !nuovoPrezzo) setNuovoPrezzo(String(d.prezzo_consigliato));
      if (d.aliquota_iva != null) setNuovaIva(String(d.aliquota_iva));
      if (d.costo != null) {
        setNuovoCosto(String(d.costo));
        setNuovoCostoOrigine(d.costo_origine ?? 'catalogo');
        setNuovoCatalogoFornitoreId(d.catalogo_fornitore_id ?? null);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articoloSelezionato, distributoreSelezionato]);

  const resetAddForm = useCallback(() => {
    setAddMode(null);
    setArticoliQuery('');
    setArticoliResults([]);
    setArticoloSelezionato(null);
    setDistributoriQuery('');
    setDistributoriResults([]);
    setDistributoreSelezionato(null);
    setNuovaDescrizione('');
    setNuovaUM('');
    setNuovaQuantita('');
    setNuovoPrezzo('');
    setNuovaIva('22');
    setNuovoSconto('');
    setNuovoCosto('');
    setNuovoCostoOrigine(null);
    setNuovoCatalogoFornitoreId(null);
    setSalvaCatalogo(false);
    setNuovoCodiceCatalogo('');
    setCatalogoFornitoreQuery('');
    setCatalogoFornitoreResults([]);
    setCatalogoFornitoreSelezionato(null);
    setCatalogoSaveError(null);
    setCatalogoArticoloCreato(null);
  }, []);

  // Azzera SOLO i campi del pannello "Salva a catalogo" di una riga esistente
  // (Annulla o successo): NON tocca rigaCatalogoArticoloCreato, la mappa
  // per-riga degli articoli gia' creati resta intatta cosi' un Annulla dopo
  // un POST riuscito + PUT fallita non porta a un ri-POST dello stesso SKU
  // alla riapertura (Finding 1, orphan dead-end). La mappa si azzera per
  // intero solo al cambio preventivo (vedi effetto su [id] sotto).
  const resetRigaCatalogoPanel = useCallback(() => {
    setRigaCatalogoPanelId(null);
    setRigaCatalogoCodice('');
    setRigaCatalogoIsServizio(true);
    setRigaCatalogoFornitoreQuery('');
    setRigaCatalogoFornitoreResults([]);
    setRigaCatalogoFornitoreSelezionato(null);
    setRigaCatalogoSaveError(null);
  }, []);

  // Reset form state when navigating to a different preventivo (avoid cross-preventivo state leak)
  useEffect(() => {
    resetAddForm();
    resetRigaCatalogoPanel();
    // Le righe (e i loro id) appartengono a UN preventivo: la mappa per-riga
    // non ha senso oltre il cambio preventivo, a differenza di Annulla.
    setRigaCatalogoArticoloCreato({});
  }, [id, resetAddForm, resetRigaCatalogoPanel]);

  // Apre il pannello "Salva a catalogo" per una riga esistente non ancora
  // collegata a un articolo (articolo_id nullo). Un solo pannello alla volta:
  // aprirne uno nuovo azzera e sostituisce i CAMPI del form eventualmente
  // gia' aperto su un'altra riga (rigaCatalogoArticoloCreato pero' NON si
  // tocca: e' per-riga, vedi commento sulla dichiarazione dello stato). Il
  // codice si precompila dall'articolo gia' creato per QUESTA riga in un
  // tentativo precedente se presente (resume del solo collegamento, stesso
  // pattern del form di aggiunta), altrimenti dal codice_articolo digitato a
  // mano (caso Walter: "ABB-SUPREMO").
  const handleApriRigaCatalogo = (r: RigaPreventivo) => {
    const creatoPerQuestaRiga = rigaCatalogoArticoloCreato[r.id];
    setRigaCatalogoPanelId(r.id);
    setRigaCatalogoCodice(creatoPerQuestaRiga ? creatoPerQuestaRiga.sku : r.codice_articolo || '');
    setRigaCatalogoIsServizio(true);
    setRigaCatalogoFornitoreQuery('');
    setRigaCatalogoFornitoreResults([]);
    setRigaCatalogoFornitoreSelezionato(null);
    setRigaCatalogoSaveError(null);
  };

  // Salva a catalogo una riga ESISTENTE del preventivo (Task T3): crea prima
  // l'articolo a magazzino (POST, come il form di aggiunta) e solo dopo
  // collega la riga (PUT articolo_id + codice_articolo). Se la POST riesce ma
  // la PUT fallisce, l'articolo creato resta in stato per rigaId
  // (rigaCatalogoArticoloCreato) cosi' un retry - anche dopo un Annulla +
  // riapertura della stessa riga - ritenta solo la PUT senza ricreare un
  // secondo articolo con lo stesso SKU: unique parziale idx_am_sku
  // (tenant_id, sku_interno) WHERE deleted_at IS NULL presente lato server
  // (modules/magazzino/migrations/versions/0001_initial.py), il duplicato
  // reale tornerebbe un 409 gestito qui sopra ma va comunque evitato.
  //
  // rigaId e' catturato all'inizio e riletto da rigaCatalogoPanelIdRef prima
  // di ogni setState che segue un await: se nel frattempo l'utente ha
  // annullato o e' passato al pannello di un'altra riga, la risposta in
  // arrivo NON deve sporcare lo stato del pannello ora aperto (Finding 1,
  // review 94af5f7). In UI il rischio e' comunque azzerato a monte: Annulla,
  // gli input del pannello e il toggle BookmarkPlus di TUTTE le righe sono
  // disabilitati mentre rigaCatalogoActionLoading e' true.
  const handleSalvaRigaACatalogo = async (r: RigaPreventivo) => {
    const rigaId = r.id;
    const codiceTrim = rigaCatalogoCodice.trim();
    const esistente = rigaCatalogoArticoloCreato[rigaId];
    const resumeLinkOnly = !!esistente && esistente.sku === codiceTrim;
    // Il fornitore preferenziale serve solo per la POST di creazione
    // dell'articolo: nel retry di solo collegamento (resumeLinkOnly) la POST
    // e' saltata, quindi non va richiesto di nuovo (vedi rigaCatalogoResumeLinkOnly
    // nel render: il campo e' disabilitato in questo caso).
    if (!codiceTrim || (!resumeLinkOnly && !rigaCatalogoFornitoreSelezionato)) return;
    setRigaCatalogoActionLoading(true);
    setRigaCatalogoSaveError(null);

    let articoloId: string | null = null;
    let articoloSku: string | null = null;
    if (esistente && esistente.sku === codiceTrim) {
      articoloId = esistente.id;
      articoloSku = esistente.sku;
    } else {
      // Fuori dal ramo di resume: il guard sopra garantisce a runtime che
      // rigaCatalogoFornitoreSelezionato sia valorizzato qui, ma non essendo
      // un'unica condizione booleana TS non lo narrowa da solo.
      if (!rigaCatalogoFornitoreSelezionato) {
        setRigaCatalogoActionLoading(false);
        return;
      }
      const res = await fetch('/api/v1/magazzino/articoli', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sku_interno: codiceTrim,
          descrizione: r.descrizione.slice(0, 500),
          fornitore_preferenziale_id: rigaCatalogoFornitoreSelezionato.id,
          is_servizio: rigaCatalogoIsServizio,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        const msg =
          res.status === 409
            ? `Codice "${codiceTrim}" gia' presente a catalogo. Scegline un altro.`
            : res.status === 403
              ? 'Permesso mancante per creare articoli a catalogo (magazzino.modifica).'
              : err?.detail || `Errore ${res.status} nella creazione a catalogo.`;
        if (rigaCatalogoPanelIdRef.current === rigaId) {
          setRigaCatalogoSaveError(msg);
        }
        setRigaCatalogoActionLoading(false);
        return;
      }
      const data = await res.json();
      articoloId = data.id;
      articoloSku = data.sku_interno;
      setRigaCatalogoArticoloCreato((prev) => ({ ...prev, [rigaId]: { id: data.id, sku: data.sku_interno } }));
    }

    const rigaRes = await fetch(`/api/v1/preventivi/${id}/righe/${rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ articolo_id: articoloId, codice_articolo: articoloSku }),
    });
    if (!rigaRes.ok) {
      // L'articolo a catalogo (se creato sopra) resta comunque salvato: solo
      // il collegamento alla riga non e' andato a buon fine. Segnala
      // l'errore invece di fallire silenziosamente, ma solo se il pannello e'
      // ancora quello della riga di partenza.
      const err = await rigaRes.json().catch(() => null);
      if (rigaCatalogoPanelIdRef.current === rigaId) {
        setRigaCatalogoSaveError(err?.detail || `Errore ${rigaRes.status} nell'aggiornamento della riga.`);
      }
      setRigaCatalogoActionLoading(false);
      return;
    }
    // Chiude il pannello solo se e' ancora sulla riga appena salvata: se nel
    // frattempo l'utente lo ha chiuso o spostato su un'altra riga, non
    // toccare quello stato (il refresh sotto aggiorna comunque la riga).
    if (rigaCatalogoPanelIdRef.current === rigaId) {
      resetRigaCatalogoPanel();
    }
    await fetchData();
    setRigaCatalogoActionLoading(false);
  };

  const handleAddRiga = async () => {
    if (!nuovaDescrizione.trim()) return;
    const qty = parseFloat(nuovaQuantita);
    if (isNaN(qty) || qty <= 0) return;
    const vuoleSalvareACatalogo = addMode === 'servizio' && !articoloSelezionato && salvaCatalogo;
    if (vuoleSalvareACatalogo && (!nuovoCodiceCatalogo.trim() || !catalogoFornitoreSelezionato)) return;
    setActionLoading(true);
    setCatalogoSaveError(null);

    // Se "Salva nel catalogo" e' selezionato: crea PRIMA l'articolo a
    // magazzino (is_servizio=true). Se fallisce (409 SKU duplicato, 403
    // permesso mancante, o altro errore), blocca tutto: NON si crea la riga
    // in modo degradato senza collegamento a catalogo.
    let catalogoArticoloId: string | null = null;
    let catalogoSku: string | null = null;
    if (vuoleSalvareACatalogo) {
      const codiceTrim = nuovoCodiceCatalogo.trim();
      if (catalogoArticoloCreato && catalogoArticoloCreato.sku === codiceTrim) {
        // Retry dopo un fallimento della sola riga: l'articolo e' gia' stato
        // creato in un tentativo precedente. Riusa id/sku senza rifare la
        // POST: un secondo tentativo con lo stesso SKU incontrerebbe il
        // vincolo unique parziale idx_am_sku (tenant_id, sku_interno) WHERE
        // deleted_at IS NULL lato server e tornerebbe un 409 evitabile.
        catalogoArticoloId = catalogoArticoloCreato.id;
        catalogoSku = catalogoArticoloCreato.sku;
      } else {
        const res = await fetch('/api/v1/magazzino/articoli', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sku_interno: codiceTrim,
            descrizione: nuovaDescrizione.trim(),
            fornitore_preferenziale_id: catalogoFornitoreSelezionato!.id,
            is_servizio: true,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => null);
          const msg =
            res.status === 409
              ? `Codice "${codiceTrim}" gia' presente a catalogo. Scegline un altro.`
              : res.status === 403
                ? 'Permesso mancante per creare articoli a catalogo (magazzino.modifica).'
                : err?.detail || `Errore ${res.status} nella creazione a catalogo.`;
          setCatalogoSaveError(msg);
          setActionLoading(false);
          return;
        }
        const data = await res.json();
        catalogoArticoloId = data.id;
        catalogoSku = data.sku_interno;
        setCatalogoArticoloCreato({ id: data.id, sku: data.sku_interno });
      }
    }

    const body: Record<string, string | number | boolean | null> = {
      descrizione: nuovaDescrizione.trim(),
      quantita: nuovaQuantita,
      confermata: true,
    };
    if (articoloSelezionato) {
      body.articolo_id = articoloSelezionato.id;
      body.codice_articolo = articoloSelezionato.sku_interno;
    } else if (catalogoArticoloId) {
      body.articolo_id = catalogoArticoloId;
      body.codice_articolo = catalogoSku;
    }
    // Tipo esplicito per la riga libera (senza articolo): dal modo di aggiunta.
    // Per le righe da catalogo il backend lo risolve dall'articolo, ignorando
    // questo valore.
    body.is_servizio = addMode === 'servizio';
    if (nuovaUM.trim()) body.unita_misura = nuovaUM.trim();
    if (nuovoPrezzo) body.prezzo_unitario = nuovoPrezzo;
    if (nuovaIva) body.iva_perc = nuovaIva;
    if (nuovoSconto) body.sconto_perc = nuovoSconto;
    if (distributoreSelezionato) body.distributore_id = distributoreSelezionato.id;
    if (nuovoCosto) {
      body.costo_unitario = nuovoCosto;
      body.costo_origine = nuovoCostoOrigine || 'manuale';
      if (nuovoCatalogoFornitoreId) body.catalogo_fornitore_id = nuovoCatalogoFornitoreId;
    }
    const rigaRes = await fetch(`/api/v1/preventivi/${id}/righe`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!rigaRes.ok) {
      // L'articolo a catalogo (se creato sopra) resta comunque salvato: solo
      // la riga di preventivo non e' stata aggiunta. Segnala l'errore invece
      // di fallire silenziosamente.
      const err = await rigaRes.json().catch(() => null);
      setCatalogoSaveError(err?.detail || `Errore ${rigaRes.status} nella creazione della riga.`);
      setActionLoading(false);
      return;
    }
    resetAddForm();
    await fetchData();
    setActionLoading(false);
  };

  const handleAddVoceACorpo = async () => {
    if (!nuovaDescrizione.trim()) return;
    setActionLoading(true);
    await fetch(`/api/v1/preventivi/${id}/righe`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        descrizione: nuovaDescrizione.trim(),
        quantita: '1',
        note: nuovaNoteACorpo.trim() || null,
        confermata: true,
        is_servizio: addMode === 'servizio',
      }),
    });
    setNuovaDescrizione('');
    setNuovaNoteACorpo('');
    setAddMode(null);
    await fetchData();
    setActionLoading(false);
  };

  const apriEditIntestazione = () => {
    setEditOggetto(preventivo?.oggetto || '');
    setEditPremessa(preventivo?.premessa || '');
    setEditCondizioni(preventivo?.condizioni_fornitura || '');
    setEditRiferimento(preventivo?.riferimento_progetto || '');
    setEditImportoACorpo(preventivo?.importo_a_corpo || '');
    setEditTipoOfferta(preventivo?.tipo_offerta || 'dettagliato');
    setEditNote(preventivo?.note || '');
    setShowEditIntestazione(true);
  };

  const salvaIntestazione = async () => {
    setActionLoading(true);
    const body: Record<string, unknown> = {
      tipo_offerta: editTipoOfferta,
      oggetto: editOggetto.trim() || null,
      premessa: editPremessa.trim() || null,
      condizioni_fornitura: editCondizioni.trim() || null,
      riferimento_progetto: editRiferimento.trim() || null,
      note: editNote.trim() || null,
    };
    if (editImportoACorpo) body.importo_a_corpo = editImportoACorpo;
    else body.importo_a_corpo = null;

    const res = await fetch(`/api/v1/preventivi/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setShowEditIntestazione(false);
      await fetchData();
    } else {
      const err = await res.json().catch(() => null);
      alert(err?.detail || `Errore ${res.status}`);
    }
    setActionLoading(false);
  };

  // Salva la modalita' di pagamento selezionata dal lookup (Task D). Si invia
  // sempre la descrizione (testo libero, no FK), stesso valore gia' inviato
  // da NuovoPreventivoVendita.tsx e mostrato as-is nel PDF/OrdineVenditaDetail.
  const handleSaveModalitaPagamento = async (descrizione: string) => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/preventivi/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ modalita_pagamento: descrizione.trim() || null }),
    });
    if (res.ok) {
      setEditingModalita(false);
      setModalitaSearch('');
      await fetchData();
    } else {
      const err = await res.json().catch(() => null);
      alert(err?.detail || `Errore ${res.status}`);
    }
    setActionLoading(false);
  };

  // Anteprima email (T5): GET /preventivi/{id}/anteprima-email restituisce
  // oggetto/corpo gia' renderizzati dal modello configurato (o default
  // hardcoded) + destinatari_default. Nessun invio reale, solo lettura.
  const fetchAnteprimaEmail = useCallback(async (): Promise<AnteprimaEmail | null> => {
    setAnteprimaLoading(true);
    setAnteprimaError(null);
    try {
      const res = await fetch(`/api/v1/preventivi/${id}/anteprima-email`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        return (await res.json()) as AnteprimaEmail;
      }
      setAnteprimaError('Anteprima email non disponibile: compila oggetto e corpo manualmente.');
    } catch {
      setAnteprimaError('Anteprima email non disponibile: compila oggetto e corpo manualmente.');
    } finally {
      setAnteprimaLoading(false);
    }
    return null;
  }, [id, token]);

  // Apertura dialog invio vendita: carica sempre l'anteprima corrente
  // (oggetto/corpo dipendono da numero/cliente/totale, possono essere
  // cambiati dall'ultima apertura). Precompila il destinatario "altro" solo
  // se il campo e' vuoto, senza toccare CC/CCN o la selezione cliente/altro.
  const handleOpenInviaVendita = useCallback(async () => {
    setShowInviaVendita(true);
    setInvioVResult(null);
    const anteprima = await fetchAnteprimaEmail();
    if (anteprima) {
      setInvioVOggetto(anteprima.oggetto);
      setInvioVCorpo(anteprima.corpo);
      if (anteprima.destinatari_default.length > 0) {
        setInvioVEmail((prev) => (prev.trim() ? prev : anteprima.destinatari_default.join(', ')));
      }
    } else {
      setInvioVOggetto('');
      setInvioVCorpo('');
    }
  }, [fetchAnteprimaEmail]);

  // Toggle dialog invio fornitore (stesso bottone apre/chiude): l'anteprima
  // viene ricaricata solo quando il dialog si sta aprendo.
  const handleToggleInvio = useCallback(async () => {
    const apertura = !showInvio;
    setShowInvio(apertura);
    if (!apertura) return;
    setInvioResult(null);
    const anteprima = await fetchAnteprimaEmail();
    if (anteprima) {
      setInvioOggetto(anteprima.oggetto);
      setInvioCorpo(anteprima.corpo);
      if (anteprima.destinatari_default.length > 0) {
        setInvioEmail((prev) => (prev.trim() ? prev : anteprima.destinatari_default[0]));
      }
    } else {
      setInvioOggetto('');
      setInvioCorpo('');
    }
  }, [showInvio, fetchAnteprimaEmail]);

  const handleInvia = async () => {
    if (!invioEmail.trim()) return;
    setActionLoading(true);
    setInvioResult(null);
    const body: Record<string, string | undefined> = {
      email_override: invioEmail.trim(),
    };
    const oggettoTrimmed = invioOggetto.trim();
    const corpoTrimmed = invioCorpo.trim();
    if (oggettoTrimmed) body.oggetto = oggettoTrimmed;
    if (corpoTrimmed) body.corpo = corpoTrimmed;
    const res = await fetch(`/api/v1/preventivi/${id}/invia-fornitore`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const data = await res.json();
      setInvioResult({ ok: data.inviato, msg: data.messaggio });
    } else {
      const err = await res.json().catch(() => null);
      setInvioResult({ ok: false, msg: err?.detail || `Errore ${res.status}` });
    }
    setActionLoading(false);
  };

  const handleApprova = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/preventivi/${id}/approva`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      if (data.ordine_id) navigate(`/ordini/${data.ordine_id}`);
      else await fetchData();
    }
    setActionLoading(false);
  };

  const handleGeneraOrdineVendita = async (firmato?: File | null) => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/ordini-vendita/da-preventivo/${id}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      // Allegato firmato opzionale: se presente lo carichiamo subito. Un errore
      // qui NON blocca (l'ordine e' creato): si potra' riprovare dal dettaglio.
      if (firmato) {
        try {
          const fd = new FormData();
          fd.append('file', firmato);
          const up = await fetch(`/api/v1/ordini-vendita/${data.id}/allegato-firmato`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!up.ok) {
            const e = await up.json().catch(() => ({}));
            alert(
              `Ordine creato. Ordine firmato NON caricato (${e.detail || up.status}): ` +
                'puoi riprovare dal dettaglio ordine.',
            );
          }
        } catch {
          alert('Ordine creato. Ordine firmato non caricato: puoi riprovare dal dettaglio ordine.');
        }
      }
      setGeneraOrdineOpen(false);
      setFileFirmato(null);
      navigate(`/ordini-vendita/${data.id}`);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || `Errore ${res.status}`);
    }
    setActionLoading(false);
  };

  const handleRiformula = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/preventivi/${id}/riformula`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      // La riformula genera una nuova versione dello stesso tipo del preventivo di origine.
      navigate(preventivo?.tipo === 'vendita' ? `/vendite/preventivi/${data.id}` : `/preventivi/${data.id}`);
    } else {
      const err = await res.json().catch(() => ({}));
      alert(err.detail || `Errore ${res.status}`);
    }
    setActionLoading(false);
  };

  const handleInviaVendita = async () => {
    setActionLoading(true);
    setInvioVResult(null);

    const destinatariTo =
      invioVDestType === 'cliente'
        ? (preventivo?.cliente_pec || preventivo?.cliente_email || '').split(/[,;\s]+/).filter(Boolean)
        : invioVEmail.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

    if (destinatariTo.length === 0) {
      setInvioVResult({ ok: false, msg: 'Destinatario mancante' });
      setActionLoading(false);
      return;
    }

    const cc = invioVCC.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    const ccn = invioVCCN.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);

    const oggettoTrimmed = invioVOggetto.trim();
    const corpoTrimmed = invioVCorpo.trim();

    const fd = new FormData();
    fd.append('canale', invioVCanale);
    fd.append('destinatari_to', JSON.stringify(destinatariTo));
    if (cc.length > 0) fd.append('cc', JSON.stringify(cc));
    if (ccn.length > 0) fd.append('ccn', JSON.stringify(ccn));
    if (oggettoTrimmed) fd.append('oggetto', oggettoTrimmed);
    if (corpoTrimmed) fd.append('corpo', corpoTrimmed);
    allegatiInvio.forEach((f) => fd.append('allegati', f));

    const res = await fetch(`/api/v1/preventivi/${id}/invia`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });

    if (res.ok) {
      const data = await res.json();
      setInvioVResult({ ok: data.sent, msg: data.message });
      setAllegatiInvio([]);
    } else {
      const err = await res.json().catch(() => null);
      setInvioVResult({ ok: false, msg: err?.detail || `Errore ${res.status}` });
    }
    setActionLoading(false);
  };

  const handleDownloadPdfVendita = async () => {
    setActionLoading(true);
    const res = await fetch(`/api/v1/preventivi/${id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preventivo_${preventivo?.numero.replace('/', '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    }
    setActionLoading(false);
  };

  const handleRifiuta = async () => {
    setActionLoading(true);
    await fetch(`/api/v1/preventivi/${id}/rifiuta`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ motivazione: null }),
    });
    await fetchData();
    setActionLoading(false);
  };

  // Elimina DAVVERO (no soft delete) la bozza: unica protezione e' lo stato
  // 'bozza' (v. elimina_preventivo_bozza), quindi la conferma qui e'
  // esplicita e nomina il numero, cosi' l'utente sa esattamente cosa sta
  // cancellando. Non affermiamo se il numero verra' rilasciato: saperlo con
  // certezza richiederebbe una chiamata apposta (se il preventivo e'
  // l'ultimo emesso della serie), che qui non facciamo per non introdurre un
  // roundtrip solo per un dettaglio del messaggio di conferma.
  const handleElimina = async () => {
    if (!preventivo) return;
    const conferma = window.confirm(
      `Eliminare DEFINITIVAMENTE il preventivo ${preventivo.numero}?\n\n` +
        "L'operazione NON e' reversibile: il documento e le sue righe " +
        'verranno cancellati e non potranno essere recuperati (non e\' ' +
        "un'archiviazione).",
    );
    if (!conferma) return;
    setActionLoading(true);
    setEliminaErrore(null);
    try {
      const res = await fetch(`/api/v1/preventivi/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        // Regola HARD tasto Indietro: torna alla lista di provenienza con
        // filtri/ricerca/pagina intatti (stato nella URL della lista, non
        // in useState di questa pagina).
        navigate(-1);
        return;
      }
      const body = await res.json().catch(() => null);
      // detail e' stringa per gli errori di dominio del service (stato non
      // piu' bozza, o preventivo collegato a una richiesta fornitore): va
      // mostrato per intero, non ingoiato.
      setEliminaErrore(typeof body?.detail === 'string' ? body.detail : `Errore ${res.status} durante l'eliminazione`);
    } catch {
      setEliminaErrore('Errore di rete: verifica la connessione e riprova.');
    } finally {
      setActionLoading(false);
    }
  };

  // Loader a pagina piena SOLO al primo caricamento (nessun preventivo ancora).
  // Ai refresh dopo un'azione su riga (fetchData -> loading=true) la pagina NON
  // deve smontarsi: altrimenti si ri-monta da zero e il browser perde la
  // posizione di scroll (torna in cima). Tenendola montata, il DOM persiste e
  // lo scroll resta dov'era. Vedi regola UX: mai perdere la posizione.
  if (loading && !preventivo) {
    return <div className="p-6 text-muted-foreground">Caricamento...</div>;
  }

  if (loadError || !preventivo) {
    return (
      <div className="p-6 text-center space-y-3">
        <p className="text-sm text-destructive">{loadError ?? 'Preventivo non trovato'}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Torna indietro
        </Button>
      </div>
    );
  }

  const badge = STATI_BADGE[preventivo.stato] || { label: preventivo.stato, className: 'bg-muted text-muted-foreground' };
  const isEditable = preventivo.stato === 'bozza' || preventivo.stato === 'in_revisione';
  // Follow-up DEC-103 (B1): un preventivo di ACQUISTO 'inviato' resta
  // azionabile per un reinvio (RFQ rispedita al fornitore, es. sollecito) e
  // per l'approvazione (approva_preventivo ora ammette anche 'inviato',
  // services.py) - altrimenti il preventivo resterebbe in un vicolo cieco.
  // Il CONTENUTO (Carica PDF, Aggiungi articoli) resta invece editable-only:
  // una RFQ gia' inviata blocca le modifiche, stesso comportamento della
  // vendita (isEditable).
  const acquistoInviabileOApprovabile =
    preventivo.tipo === 'acquisto' && (isEditable || preventivo.stato === 'inviato');
  const righeConfermate = preventivo.righe.filter((r) => r.confermata).length;

  // Valore "effettivo" di un campo riga: se la riga e' in edit su quel campo,
  // usa il valore in digitazione (editingValue), altrimenti il valore salvato.
  // Cosi' i totali si ricalcolano live mentre l'utente digita, senza aspettare
  // il salvataggio + fetchData.
  const getEffectiveField = (
    r: RigaPreventivo,
    campo: 'quantita' | 'prezzo_unitario' | 'iva_perc' | 'sconto_perc' | 'costo_unitario',
  ): string | null => {
    if (editingField?.rigaId === r.id && editingField?.campo === campo) return editingValue;
    return r[campo] ?? null;
  };

  // Righe con i valori effettivi (live) + importo/margine ricalcolati client-side
  // con la stessa formula del backend (vedi calcolaImportoRiga/calcolaMargineRiga).
  const sogliaMargine = preventivo.soglia_margine_perc != null ? parseFloat(preventivo.soglia_margine_perc) : null;
  const righeLive = preventivo.righe.map((r) => {
    const effQuantita = getEffectiveField(r, 'quantita');
    const effPrezzo = getEffectiveField(r, 'prezzo_unitario');
    const effSconto = getEffectiveField(r, 'sconto_perc');
    const effCosto = getEffectiveField(r, 'costo_unitario');
    const effIva = getEffectiveField(r, 'iva_perc');
    const importoRiga = calcolaImportoRiga(effQuantita, effPrezzo, effSconto);
    const margine = calcolaMargineRiga(importoRiga, effQuantita, effCosto);
    const sottoSoglia = sogliaMargine != null && margine.marginePerc != null && margine.marginePerc < sogliaMargine;
    return { r, effQuantita, effPrezzo, effSconto, effCosto, effIva, importoRiga, margine, sottoSoglia };
  });
  const righeConfermateLive = righeLive.filter((x) => x.r.confermata);

  // Breakdown IVA/totale: imponibileNetto replica _ricalcola_totale_preventivo
  // (SUM(importo_riga confermate) * (1 - sconto_globale%), arrotondato a 0.01),
  // ma usando i valori live delle righe (non l'ultimo importo_totale salvato).
  // L'IVA si calcola per riga confermata sull'imponibile scontato (solo frontend,
  // il backend non calcola IVA).
  const scontoGlobalePerc = parseFloat(preventivo.sconto_globale_perc ?? '0') || 0;
  const imponibileNetto = round2(
    righeConfermateLive.reduce((acc, x) => acc + x.importoRiga, 0) * (1 - scontoGlobalePerc / 100),
  );
  const ivaTotale = righeConfermateLive.reduce(
    (acc, x) =>
      acc +
      x.importoRiga * (1 - scontoGlobalePerc / 100) * (parseFloat(x.effIva || '0') / 100),
    0,
  );
  const totaleDocumento = imponibileNetto + ivaTotale;
  // Gate su dato salvato, il buffer di edit non deve abilitare azioni.
  // Usa righe.prezzo_unitario persisted, non effPrezzo dal buffer inline-edit.
  const hasAllPrices = preventivo.righe
    .filter((r) => r.confermata)
    .every((r) => parseFloat(r.prezzo_unitario) > 0);

  // Margine/guadagno totali (netto IVA), sul modello del calcolo backend in
  // preventivi.py (righe confermate con costo noto; ricavo = imponibile netto).
  const righeConCosto = righeConfermateLive.filter(
    (x) => x.effCosto != null && x.effCosto !== '' && Number.isFinite(parseFloat(x.effCosto)),
  );
  let guadagnoNettoTotale: number | null = null;
  let margineTotalePerc: number | null = null;
  if (righeConCosto.length > 0) {
    const costoTotale = righeConCosto.reduce(
      (acc, x) => acc + parseFloat(x.effCosto as string) * (parseFloat(x.effQuantita || '0') || 0),
      0,
    );
    guadagnoNettoTotale = round2(imponibileNetto - costoTotale);
    if (imponibileNetto > 0) margineTotalePerc = round2((guadagnoNettoTotale / imponibileNetto) * 100);
  }
  const righeSottoSogliaCount = righeConfermateLive.filter((x) => x.sottoSoglia).length;

  // Sezioni gia' usate nel preventivo (Feature E, Task 11): suggerite nel
  // datalist del campo Sezione inline, per riusare la stessa etichetta su
  // piu' righe senza ridigitarla (i subtotali PDF raggruppano per stringa
  // esatta, un typo separerebbe due sezioni identiche in apparenza).
  const sezioniUsate = Array.from(
    new Set(
      preventivo.righe
        .map((r) => r.sezione)
        .filter((s): s is string => !!s && s.trim() !== ''),
    ),
  ).sort();

  // Raggruppamento righe per sezione nella tabella UI (rifinitura Walter,
  // 2026-07-19): rispecchia _raggruppa_righe_per_sezione del PDF generator
  // (modules/ordini/ordini/backend/pdf/generator.py) - nuovo gruppo SOLO
  // quando la sezione cambia rispetto alla riga precedente (preserva
  // l'ordine, non ordina/aggrega per nome; sezioni non contigue = piu'
  // blocchi separati). I gruppi con sezione vuota/nulla (righe senza
  // sezione) non ricevono intestazione/subtotale, esattamente come nel
  // template PDF quando `gruppo.sezione` e' falsy. Righe di sola
  // visualizzazione: NON alterano l'indice `idx` di righeLive usato dal
  // drag & drop (vincolo drag), che resta legato solo alle righe dati.
  const hasSezioni = preventivo.righe.some((r) => !!r.sezione && r.sezione.trim() !== '');
  const gruppoSezioneById = new Map<
    string,
    { sezione: string; isInizioGruppo: boolean; isFineGruppo: boolean; subtotaleGruppo: number }
  >();
  // Id di tutte le righe di ciascun gruppo, indicizzato per l'id della riga di
  // inizio-gruppo: usato dalla rinomina via banda di intestazione (Walter,
  // 2026-07-20) per sapere quali righe aggiornare con la PUT sezione.
  const gruppoRigheIdsByInizioId = new Map<string, string[]>();
  if (hasSezioni) {
    let i = 0;
    while (i < righeLive.length) {
      const sez = righeLive[i].r.sezione || null;
      let j = i;
      let subtotale = 0;
      while (j < righeLive.length && (righeLive[j].r.sezione || null) === sez) {
        subtotale += righeLive[j].importoRiga;
        j += 1;
      }
      if (sez) {
        const idsGruppo = righeLive.slice(i, j).map((x) => x.r.id);
        gruppoRigheIdsByInizioId.set(righeLive[i].r.id, idsGruppo);
        for (let k = i; k < j; k += 1) {
          gruppoSezioneById.set(righeLive[k].r.id, {
            sezione: sez,
            isInizioGruppo: k === i,
            isFineGruppo: k === j - 1,
            subtotaleGruppo: subtotale,
          });
        }
      }
      i = j;
    }
  }

  // Anteprima live della riga in aggiunta (form vendita prodotto/servizio):
  // valida solo se qta e prezzo sono numeri digitati (evita '-' su '0,00' fuorviante).
  const nuovaRigaPreviewValida =
    nuovaQuantita.trim() !== '' &&
    nuovoPrezzo.trim() !== '' &&
    Number.isFinite(parseFloat(nuovaQuantita)) &&
    Number.isFinite(parseFloat(nuovoPrezzo));
  const nuovaRigaImportoPreview = calcolaImportoRiga(nuovaQuantita, nuovoPrezzo, nuovoSconto);

  const skuInRighe = new Set(preventivo.righe.map((r) => r.codice_articolo).filter(Boolean));
  const emailDestinatario = preventivo.fornitore_pec || preventivo.fornitore_email;

  return (
    <div className="p-6 space-y-6 w-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Preventivo {preventivo.numero}
          </h1>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
            {preventivo.scaduto && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" title="Validita' scaduta">
                Scaduto
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {date(preventivo.data_preventivo)}
            </span>
            {preventivo.data_validita && (
              <span className="text-sm text-muted-foreground">
                Risposta entro: {date(preventivo.data_validita)}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap justify-end">
          {preventivo.tipo === 'vendita' && (
            <>
              <Button
                variant="outline"
                onClick={handleDownloadPdfVendita}
                disabled={actionLoading || preventivo.righe.length === 0}
              >
                Scarica PDF
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleOpenInviaVendita()}
                disabled={actionLoading || preventivo.righe.length === 0}
              >
                Invia preventivo
              </Button>
              <Button
                onClick={() => {
                  setFileFirmato(null);
                  setGeneraOrdineOpen(true);
                }}
                disabled={
                  actionLoading ||
                  preventivo.stato === 'ordine_generato' ||
                  preventivo.stato === 'rifiutato'
                }
              >
                Genera ordine di vendita
              </Button>
            </>
          )}
          {(preventivo.scaduto || preventivo.stato === 'inviato') && (
            <Button
              variant="outline"
              onClick={() => void handleRiformula()}
              disabled={actionLoading}
              title="Crea una nuova versione del preventivo"
            >
              Riformula
            </Button>
          )}
          {/* Disponibile SEMPRE, su qualsiasi stato (anche bozza): a differenza
              di Riformula non e' una nuova versione dello stesso documento, e'
              un documento nuovo per un altro cliente/fornitore (decisione
              esplicita di Walter, Task 7). */}
          <Button
            variant="outline"
            onClick={() => setDuplicaOpen(true)}
            disabled={actionLoading}
            title="Crea una copia di questo preventivo per un altro intestatario"
          >
            {preventivo.tipo === 'vendita' ? 'Duplica per altro cliente' : 'Duplica per altro fornitore'}
          </Button>
          {acquistoInviabileOApprovabile && (
            <Button
              variant="outline"
              onClick={() => void handleToggleInvio()}
              disabled={actionLoading || preventivo.righe.length === 0}
            >
              Invia al fornitore
            </Button>
          )}
          {isEditable && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                className="hidden"
                onChange={handleUploadPdf}
              />
              {preventivo.tipo === 'acquisto' && (
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={actionLoading}
                >
                  Carica PDF
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setShowCatalogo(!showCatalogo)}
                disabled={actionLoading}
              >
                {showCatalogo ? 'Nascondi catalogo' : 'Aggiungi articoli'}
              </Button>
            </>
          )}
          {acquistoInviabileOApprovabile && (
            <Button
              onClick={handleApprova}
              disabled={actionLoading || righeConfermate === 0 || !hasAllPrices}
              title={!hasAllPrices ? 'Inserisci i prezzi su tutte le righe confermate' : ''}
            >
              Approva e genera ordine
            </Button>
          )}
          {['bozza', 'in_revisione', 'inviato'].includes(preventivo.stato) && (
            <Button
              variant="destructive"
              onClick={handleRifiuta}
              disabled={actionLoading}
            >
              Rifiuta
            </Button>
          )}
          {/* Cancellazione reale (no soft delete): SOLO in bozza, unica
              protezione lato backend (v. elimina_preventivo_bozza). */}
          {preventivo.stato === 'bozza' && (
            <Button
              variant="destructive"
              onClick={() => void handleElimina()}
              disabled={actionLoading}
              title="Elimina definitivamente questa bozza"
            >
              Elimina bozza
            </Button>
          )}
        </div>
      </div>

      {eliminaErrore && (
        <p className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded px-3 py-2">
          {eliminaErrore}
        </p>
      )}

      {/* Intestatario: Fornitore per acquisto, Cliente per vendita */}
      {preventivo.tipo === 'acquisto' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">Fornitore</h2>
            {/* Cambio intestatario: SOLO in bozza, unica protezione lato
                backend (v. cambia_intestatario_preventivo). */}
            {preventivo.stato === 'bozza' && (
              <Button variant="outline" size="sm" onClick={() => setCambiaIntestatarioOpen(true)}>
                Cambia fornitore
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <span className="text-lg font-semibold text-foreground">
                {preventivo.fornitore_nome || 'Fornitore sconosciuto'}
              </span>
            </div>
            {preventivo.fornitore_partita_iva && (
              <div>
                <span className="text-muted-foreground">P.IVA:</span>{' '}
                <span className="font-mono">{preventivo.fornitore_partita_iva}</span>
              </div>
            )}
            {preventivo.fornitore_email && (
              <div>
                <span className="text-muted-foreground">Email:</span>{' '}
                <a href={`mailto:${preventivo.fornitore_email}`} className="text-primary hover:underline">
                  {preventivo.fornitore_email}
                </a>
              </div>
            )}
            {preventivo.fornitore_pec && (
              <div>
                <span className="text-muted-foreground">PEC:</span>{' '}
                <a href={`mailto:${preventivo.fornitore_pec}`} className="text-primary hover:underline">
                  {preventivo.fornitore_pec}
                </a>
              </div>
            )}
            {preventivo.fornitore_telefono && (
              <div>
                <span className="text-muted-foreground">Tel:</span>{' '}
                {preventivo.fornitore_telefono}
              </div>
            )}
          </div>
        </Card>
      )}

      {preventivo.tipo === 'vendita' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">Cliente</h2>
            {/* Cambio intestatario: SOLO in bozza, unica protezione lato
                backend (v. cambia_intestatario_preventivo). */}
            {preventivo.stato === 'bozza' && (
              <Button variant="outline" size="sm" onClick={() => setCambiaIntestatarioOpen(true)}>
                Cambia cliente
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <span className="text-lg font-semibold text-foreground">
                {preventivo.cliente_nome || 'Cliente sconosciuto'}
              </span>
            </div>
            {preventivo.cliente_partita_iva && (
              <div>
                <span className="text-muted-foreground">P.IVA:</span>{' '}
                <span className="font-mono">{preventivo.cliente_partita_iva}</span>
              </div>
            )}
            {preventivo.cliente_email && (
              <div>
                <span className="text-muted-foreground">Email:</span>{' '}
                <a href={`mailto:${preventivo.cliente_email}`} className="text-primary hover:underline">
                  {preventivo.cliente_email}
                </a>
              </div>
            )}
            {preventivo.cliente_pec && (
              <div>
                <span className="text-muted-foreground">PEC:</span>{' '}
                <a href={`mailto:${preventivo.cliente_pec}`} className="text-primary hover:underline">
                  {preventivo.cliente_pec}
                </a>
              </div>
            )}
            {preventivo.cliente_telefono && (
              <div>
                <span className="text-muted-foreground">Tel:</span>{' '}
                {preventivo.cliente_telefono}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Premessa e note/clausole (tutti i tipi vendita; importo a corpo solo a_corpo/misto) */}
      {preventivo.tipo === 'vendita' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-foreground">
              Premessa e note/clausole
              {(preventivo.tipo_offerta === 'a_corpo' || preventivo.tipo_offerta === 'misto') && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                  {preventivo.tipo_offerta === 'a_corpo' ? 'A corpo' : 'Misto'}
                </span>
              )}
            </h2>
            {isEditable && (
              <Button variant="outline" size="sm" onClick={apriEditIntestazione}>
                Modifica intestazione
              </Button>
            )}
          </div>
          <div className="space-y-3 text-sm">
            {(preventivo.tipo_offerta === 'a_corpo' || preventivo.tipo_offerta === 'misto') && (
              preventivo.oggetto ? (
                <div>
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Oggetto</div>
                  <div className="whitespace-pre-wrap">{preventivo.oggetto}</div>
                </div>
              ) : (
                <div className="text-xs text-muted-foreground italic">Oggetto non compilato</div>
              )
            )}
            {(preventivo.tipo_offerta === 'a_corpo' || preventivo.tipo_offerta === 'misto') && preventivo.riferimento_progetto && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Riferimento progetto</div>
                <div>{preventivo.riferimento_progetto}</div>
              </div>
            )}
            {preventivo.premessa && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Premessa</div>
                <div className="whitespace-pre-wrap">{preventivo.premessa}</div>
              </div>
            )}
            {preventivo.tipo_offerta === 'a_corpo' && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Totale a corpo</div>
                <div className="text-lg font-semibold">
                  {preventivo.importo_a_corpo
                    ? currency(preventivo.importo_a_corpo)
                    : <span className="text-amber-600 italic">non impostato</span>}
                </div>
              </div>
            )}
            {preventivo.condizioni_fornitura && (
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Condizioni di fornitura</div>
                <div className="whitespace-pre-wrap text-xs">{preventivo.condizioni_fornitura}</div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Dialog modifica intestazione contrattuale */}
      {showEditIntestazione && preventivo.tipo === 'vendita' && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 50, padding: '16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEditIntestazione(false); }}
        >
          <Card className="p-6 space-y-4 max-w-2xl w-full" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Modifica intestazione contrattuale</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowEditIntestazione(false)}>Chiudi</Button>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Tipo offerta</label>
              <select
                className="w-full border rounded-md p-2"
                value={editTipoOfferta}
                onChange={(e) => setEditTipoOfferta(e.target.value as 'a_corpo' | 'dettagliato' | 'misto')}
              >
                <option value="dettagliato">Dettagliato (righe qta/prezzo/IVA/sconto)</option>
                <option value="a_corpo">A corpo (totale forfettario)</option>
                <option value="misto">Misto (a corpo + righe dettagliate)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Oggetto</label>
              <textarea
                className="w-full border rounded-md p-2 min-h-16 text-sm"
                value={editOggetto}
                onChange={(e) => setEditOggetto(e.target.value)}
                placeholder="Preventivo per servizi di..."
                maxLength={2000}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Riferimento progetto</label>
              <Input
                value={editRiferimento}
                onChange={(e) => setEditRiferimento(e.target.value)}
                placeholder="es. PR Calabria FESR-FSE 2021-2027 RSO 2.6"
                maxLength={500}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Premessa</label>
              <textarea
                className="w-full border rounded-md p-2 min-h-20 text-sm"
                value={editPremessa}
                onChange={(e) => setEditPremessa(e.target.value)}
                placeholder="Con la presente, in riscontro..."
                maxLength={5000}
              />
            </div>

            {(editTipoOfferta === 'a_corpo' || editTipoOfferta === 'misto') && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Importo a corpo (totale forfettario)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={editImportoACorpo}
                  onChange={(e) => setEditImportoACorpo(e.target.value)}
                  placeholder="33500.00"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Condizioni di fornitura</label>
              <textarea
                className="w-full border rounded-md p-2 min-h-24 text-sm"
                value={editCondizioni}
                onChange={(e) => setEditCondizioni(e.target.value)}
                placeholder="Validita 90 gg..."
                maxLength={5000}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Note</label>
              <textarea
                className="w-full border rounded-md p-2 min-h-16 text-sm"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
                placeholder="Note aggiuntive..."
                maxLength={10000}
              />
              <p className="text-xs text-muted-foreground">
                Mostrata in fondo al preventivo, vicino al totale.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button variant="ghost" onClick={() => setShowEditIntestazione(false)} disabled={actionLoading}>
                Annulla
              </Button>
              <Button onClick={salvaIntestazione} disabled={actionLoading}>
                {actionLoading ? 'Salvataggio...' : 'Salva intestazione'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Pannello invio email */}
      {showInviaVendita && preventivo.tipo === 'vendita' && (
        <div
          style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 50, padding: '16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowInviaVendita(false); }}
        >
          <Card className="p-6 space-y-4 max-w-lg w-full" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Invia preventivo</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowInviaVendita(false)}>
                Chiudi
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Genera il PDF del preventivo e invia al cliente. WhatsApp e Telegram in arrivo.
            </p>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Destinatario</label>
              <select
                className="w-full border rounded-md p-2"
                value={invioVDestType}
                onChange={(e) => setInvioVDestType(e.target.value as 'cliente' | 'altro')}
              >
                <option value="cliente">
                  Cliente: {preventivo.cliente_pec || preventivo.cliente_email || '(nessuna email in anagrafica)'}
                </option>
                <option value="altro">Altro destinatario...</option>
              </select>
            </div>

            {invioVDestType === 'altro' && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Email destinatario *</label>
                <Input
                  type="email"
                  value={invioVEmail}
                  onChange={(e) => setInvioVEmail(e.target.value)}
                  placeholder="destinatario@dominio.it"
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Canale</label>
              <select
                className="w-full border rounded-md p-2"
                value={invioVCanale}
                onChange={(e) => setInvioVCanale(e.target.value as 'email' | 'whatsapp' | 'telegram')}
              >
                <option value="email">Email</option>
                <option value="whatsapp" disabled>WhatsApp (in arrivo)</option>
                <option value="telegram" disabled>Telegram (in arrivo)</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                CC (copia visibile, separati da virgola o spazio)
              </label>
              <Input
                value={invioVCC}
                onChange={(e) => setInvioVCC(e.target.value)}
                placeholder="consulente@dominio.it, altro@dominio.it"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                CCN (copia nascosta)
              </label>
              <Input
                value={invioVCCN}
                onChange={(e) => setInvioVCCN(e.target.value)}
                placeholder="archivio@dominio.it"
              />
            </div>

            {anteprimaLoading && (
              <p className="text-xs text-muted-foreground">Caricamento anteprima email...</p>
            )}
            {anteprimaError && (
              <p className="text-xs text-amber-600 dark:text-amber-400">{anteprimaError}</p>
            )}

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Oggetto</label>
              <Input
                value={invioVOggetto}
                onChange={(e) => setInvioVOggetto(e.target.value)}
                placeholder="Oggetto email"
                disabled={anteprimaLoading}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Corpo email</label>
              <textarea
                className="w-full border rounded-md p-2"
                rows={10}
                value={invioVCorpo}
                onChange={(e) => setInvioVCorpo(e.target.value)}
                placeholder="Testo del messaggio..."
                disabled={anteprimaLoading}
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium">Allegati (oltre al PDF)</label>
              <input
                type="file"
                multiple
                onChange={(e) => {
                  const nuovi = Array.from(e.target.files ?? []);
                  setAllegatiInvio((prev) => [...prev, ...nuovi]);
                  e.target.value = '';
                }}
              />
              <ul className="mt-1 space-y-1">
                {allegatiInvio.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm">
                    <span className="truncate">{f.name} ({Math.round(f.size / 1024)} KB)</span>
                    <button
                      type="button"
                      onClick={() => setAllegatiInvio((p) => p.filter((_, j) => j !== i))}
                    >
                      Rimuovi
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-muted-foreground">
              Il PDF del preventivo viene allegato automaticamente, insieme agli eventuali allegati aggiuntivi selezionati sopra.
            </p>

            <div className="flex items-center gap-3 justify-end">
              {invioVResult && (
                <span className={`text-xs ${invioVResult.ok ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                  {invioVResult.msg}
                </span>
              )}
              <Button
                variant="ghost"
                onClick={() => setShowInviaVendita(false)}
                disabled={actionLoading}
              >
                Annulla
              </Button>
              <Button
                onClick={handleInviaVendita}
                disabled={actionLoading || (!invioVOggetto.trim() && !invioVCorpo.trim() && !!anteprimaError)}
                title={!invioVOggetto.trim() && !invioVCorpo.trim() && anteprimaError ? 'Compila oggetto o corpo email' : undefined}
              >
                {actionLoading ? 'Invio...' : 'Invia'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {showInvio && acquistoInviabileOApprovabile && (
        <Card className="p-4 space-y-3 border-primary/30">
          <h2 className="text-sm font-semibold text-foreground">
            Invia richiesta al fornitore
          </h2>
          <p className="text-xs text-muted-foreground">
            Invia un'email con la lista degli articoli e le quantita richieste.
            Il fornitore rispondera con i prezzi da inserire nelle righe.
          </p>
          {anteprimaLoading && (
            <p className="text-xs text-muted-foreground">Caricamento anteprima email...</p>
          )}
          {anteprimaError && (
            <p className="text-xs text-amber-600 dark:text-amber-400">{anteprimaError}</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground">Destinatario *</label>
              <Input
                type="email"
                value={invioEmail}
                onChange={(e) => setInvioEmail(e.target.value)}
                placeholder="email@fornitore.it"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Oggetto</label>
              <Input
                value={invioOggetto}
                onChange={(e) => setInvioOggetto(e.target.value)}
                placeholder="Oggetto email"
                className="mt-1"
                disabled={anteprimaLoading}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Corpo email</label>
            <textarea
              className="w-full border rounded-md p-2"
              rows={10}
              value={invioCorpo}
              onChange={(e) => setInvioCorpo(e.target.value)}
              placeholder="Testo del messaggio..."
              disabled={anteprimaLoading}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleInvia}
              disabled={actionLoading || !invioEmail.trim()}
              size="sm"
            >
              Invia email
            </Button>
            {!emailDestinatario && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                Nessuna email/PEC in anagrafica fornitore
              </span>
            )}
            {invioResult && (
              <span className={`text-xs ${invioResult.ok ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                {invioResult.msg}
              </span>
            )}
          </div>
        </Card>
      )}

      {/* Info condizioni */}
      <Card className="p-4">
        <h2 className="text-sm font-semibold text-foreground mb-3">Riepilogo</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          {preventivo.tipo_offerta === 'a_corpo' ? (
            <>
              <div>
                <span className="text-muted-foreground">Totale a corpo (IVA esclusa):</span>{' '}
                <span className="font-semibold">
                  {preventivo.importo_a_corpo
                    ? currency(preventivo.importo_a_corpo)
                    : <span className="text-amber-600 italic">non impostato</span>}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Voci di servizio:</span>{' '}
                {preventivo.righe.length}
              </div>
            </>
          ) : (
            <>
              <div>
                <span className="text-muted-foreground">Imponibile (netto sconti):</span>{' '}
                <span className="font-semibold">{currency(imponibileNetto.toFixed(2))}</span>
              </div>
              <div>
                <span className="text-muted-foreground">IVA:</span>{' '}
                <span className="font-semibold">{currency(ivaTotale.toFixed(2))}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Totale documento (IVA inclusa):</span>{' '}
                <span className="font-semibold">{currency(totaleDocumento.toFixed(2))}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Righe:</span>{' '}
                {righeConfermate}/{preventivo.righe.length} confermate
              </div>
            </>
          )}
          {!hasAllPrices && righeConfermate > 0 && (
            <div className="col-span-2 text-amber-600 dark:text-amber-400 text-xs">
              Alcune righe confermate non hanno ancora il prezzo — inseriscilo cliccando sulla colonna Prezzo
            </div>
          )}
          {preventivo.richiesta_id && (
            <div className="col-span-2">
              <span className="text-muted-foreground">RDP collegata:</span>{' '}
              <button
                onClick={() => navigate(`/richieste/${preventivo.richiesta_id}`)}
                className="text-primary hover:underline font-medium"
              >
                Vai alla richiesta
              </button>
            </div>
          )}
          {preventivo.termini_pagamento && (
            <div>
              <span className="text-muted-foreground">Termini pagamento:</span>{' '}
              {preventivo.termini_pagamento}
            </div>
          )}
          <div className={editingModalita ? 'col-span-2' : ''}>
            <span className="text-muted-foreground">Modalita' di pagamento:</span>{' '}
            {isEditable && editingModalita ? (
              <span className="inline-flex items-center gap-1 align-middle">
                <Combobox
                  value={modalitaSearch}
                  onInputChange={(v) => {
                    setModalitaSearch(v);
                    setModalitaDirty(true);
                  }}
                  onSelect={(opt) => void handleSaveModalitaPagamento(opt.label)}
                  options={modalitaPagamentoOpts
                    .filter(
                      (m) =>
                        !modalitaDirty ||
                        m.descrizione.toLowerCase().includes(modalitaSearch.toLowerCase()),
                    )
                    .map<ComboboxOption>((m) => ({ value: m.id, label: m.descrizione, sublabel: m.codice }))}
                  placeholder="Cerca modalita' di pagamento..."
                  className="max-w-xs inline-block"
                  autoFocus
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs px-1"
                  onClick={() => { setEditingModalita(false); setModalitaSearch(''); }}
                >
                  Annulla
                </Button>
              </span>
            ) : (
              <button
                type="button"
                disabled={!isEditable}
                onClick={() => {
                  if (!isEditable) return;
                  setModalitaSearch(preventivo.modalita_pagamento || '');
                  setModalitaDirty(false);
                  setEditingModalita(true);
                  void fetchLookups();
                }}
                className={`group inline-flex items-center gap-1 ${isEditable ? 'cursor-pointer hover:underline' : ''}`}
                title={isEditable ? "Clicca per modificare" : undefined}
              >
                <span>{preventivo.modalita_pagamento || '-'}</span>
                {isEditable && (
                  <Pencil size={12} className="shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100" />
                )}
              </button>
            )}
          </div>
          {preventivo.spese_spedizione && (
            <div>
              <span className="text-muted-foreground">Spese spedizione:</span>{' '}
              {currency(preventivo.spese_spedizione)}
            </div>
          )}
          {preventivo.minimo_ordine && (
            <div>
              <span className="text-muted-foreground">Minimo ordine:</span>{' '}
              {currency(preventivo.minimo_ordine)}
            </div>
          )}
          {preventivo.tempo_consegna_giorni != null && (
            <div>
              <span className="text-muted-foreground">Consegna:</span>{' '}
              {preventivo.tempo_consegna_giorni} giorni
            </div>
          )}
          {preventivo.file_pdf_path && (
            <div className="col-span-2">
              <span className="text-muted-foreground">PDF fornitore caricato</span>
            </div>
          )}
          {preventivo.note && (
            <div className="col-span-2">
              <span className="text-muted-foreground">Note:</span> {preventivo.note}
            </div>
          )}
        </div>
      </Card>

      {/* Pannello aggiunta - acquisto: catalogo fornitore */}
      {showCatalogo && isEditable && preventivo.tipo === 'acquisto' && (
        <Card className="p-4 space-y-3 border-accent/30">
          <h2 className="text-sm font-semibold text-foreground">
            Catalogo fornitore
          </h2>
          <Input
            placeholder="Cerca per SKU o descrizione..."
            value={catalogoSearch}
            onChange={(e) => setCatalogoSearch(e.target.value)}
          />
          {catalogo.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun articolo trovato</p>
          ) : (
            <div className="max-h-56 overflow-y-auto border border-border rounded">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                  <tr className="border-b border-border">
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">SKU</th>
                    <th className="text-left px-3 py-1.5 text-muted-foreground font-medium">Descrizione</th>
                    <th className="text-right px-3 py-1.5 text-muted-foreground font-medium w-24">Q.ta</th>
                    <th className="px-3 py-1.5 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {catalogo.map((item) => {
                    const alreadyAdded = skuInRighe.has(item.sku);
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-border ${alreadyAdded ? 'opacity-40' : 'hover:bg-muted/30'}`}
                      >
                        <td className="px-3 py-1.5 font-mono text-xs">{item.sku}</td>
                        <td className="px-3 py-1.5">{item.descrizione || '-'}</td>
                        <td className="px-3 py-1.5">
                          {!alreadyAdded && (
                            <Input
                              type="number"
                              min="1"
                              step="1"
                              value={catalogoQty[item.id] ?? ''}
                              onChange={(e) =>
                                setCatalogoQty((prev) => ({ ...prev, [item.id]: e.target.value }))
                              }
                              placeholder="Q.ta"
                              className="w-20 text-right text-sm ml-auto"
                            />
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-center">
                          {alreadyAdded ? (
                            <span className="text-xs text-muted-foreground">Aggiunto</span>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleAddFromCatalogo(item)}
                              disabled={!catalogoQty[item.id] || parseFloat(catalogoQty[item.id]) <= 0 || actionLoading}
                              className="text-xs"
                            >
                              Aggiungi
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Pannello aggiunta - vendita: prodotto da magazzino o servizio libero */}
      {showCatalogo && isEditable && preventivo.tipo === 'vendita' && (
        <Card className="p-4 space-y-4 border-accent/30">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Aggiungi riga al preventivo</h2>
            {addMode && (
              <Button variant="ghost" size="sm" onClick={resetAddForm}>
                Annulla
              </Button>
            )}
          </div>

          {!addMode && preventivo.tipo_offerta === 'a_corpo' && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAddMode('servizio')}>
                + Aggiungi voce di servizio
              </Button>
              <p className="text-xs text-muted-foreground self-center ml-2">
                Le voci a corpo hanno solo titolo + descrizione. Nessun prezzo per voce: il totale e' uno solo (Totale a corpo).
              </p>
            </div>
          )}

          {!addMode && (preventivo.tipo_offerta === 'dettagliato' || preventivo.tipo_offerta === 'misto') && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAddMode('prodotto')}>
                + Aggiungi prodotto (da catalogo)
              </Button>
              <Button variant="outline" onClick={() => setAddMode('servizio')}>
                + Aggiungi servizio (voce libera)
              </Button>
            </div>
          )}

          {addMode === 'prodotto' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Cerca un articolo dal magazzino. La selezione popola codice, descrizione e unita di misura.
              </p>
              {!articoloSelezionato ? (
                <Combobox
                  value={articoliQuery}
                  onInputChange={setArticoliQuery}
                  onSelect={(opt) => {
                    const a = articoliResults.find((x) => x.id === opt.value);
                    if (!a) return;
                    setArticoloSelezionato(a);
                    setNuovaDescrizione(a.descrizione);
                    setNuovaUM(a.unita_misura || '');
                  }}
                  options={articoliResults.map<ComboboxOption>((a) => ({
                    value: a.id,
                    label: `${a.sku_interno} - ${a.descrizione}`,
                    sublabel: [a.unita_misura ? `UM: ${a.unita_misura}` : '', a.is_servizio ? 'Servizio' : '']
                      .filter(Boolean)
                      .join(' | '),
                  }))}
                  loading={articoliLoading}
                  placeholder="Digita SKU o descrizione articolo..."
                />
              ) : (
                <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                  <span>
                    <span className="font-mono text-xs">{articoloSelezionato.sku_interno}</span>
                    {' - '}
                    {articoloSelezionato.descrizione}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => {
                    setArticoloSelezionato(null);
                    setNuovaDescrizione('');
                    setNuovaUM('');
                  }}>
                    Cambia
                  </Button>
                </div>
              )}

              {articoloSelezionato && (
                <div>
                  <label className="text-xs text-muted-foreground">
                    Distributore (fornitore) - fonte del costo
                  </label>
                  {!distributoreSelezionato ? (
                    <Combobox
                      value={distributoriQuery}
                      onInputChange={setDistributoriQuery}
                      onSelect={(opt) => {
                        const d = distributoriResults.find((x) => x.id === opt.value);
                        if (!d) return;
                        setDistributoreSelezionato(d);
                      }}
                      options={distributoriResults.map<ComboboxOption>((d) => ({
                        value: d.id,
                        label: d.ragione_sociale || [d.nome, d.cognome].filter(Boolean).join(' ') || '-',
                      }))}
                      loading={distributoriLoading}
                      placeholder="Digita nome del distributore..."
                      onCreateNew={() => setQuickCreateTarget('distributore')}
                      createNewLabel="Crea nuovo fornitore..."
                    />
                  ) : (
                    <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                      <span>{distributoreSelezionato.ragione_sociale || [distributoreSelezionato.nome, distributoreSelezionato.cognome].filter(Boolean).join(' ')}</span>
                      <Button variant="ghost" size="sm" onClick={() => {
                        setDistributoreSelezionato(null);
                        setNuovoCosto('');
                        setNuovoCostoOrigine(null);
                        setNuovoCatalogoFornitoreId(null);
                      }}>
                        Cambia
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    Il costo si precompila dal listino del distributore (per SKU); se assente, inseriscilo a mano.
                  </p>
                </div>
              )}
            </div>
          )}

          {addMode === 'servizio' && preventivo.tipo_offerta === 'a_corpo' && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Voce a corpo: solo titolo + descrizione lunga. Il prezzo non e' per voce ma e' uno solo (Totale a corpo, vedi intestazione contrattuale).
              </p>
              <div>
                <label className="text-xs text-muted-foreground">Titolo della voce *</label>
                <Input
                  value={nuovaDescrizione}
                  onChange={(e) => setNuovaDescrizione(e.target.value)}
                  placeholder="es. Creazione di prodotti e/o supporti divulgativi"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Descrizione lunga (opzionale)</label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm"
                  rows={3}
                  value={nuovaNoteACorpo}
                  onChange={(e) => setNuovaNoteACorpo(e.target.value)}
                  placeholder="Ideazione e produzione di contenuti divulgativi in formato cartaceo e digitale..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={resetAddForm}>Annulla</Button>
                <Button
                  onClick={handleAddVoceACorpo}
                  disabled={actionLoading || !nuovaDescrizione.trim()}
                >
                  {actionLoading ? 'Aggiungo...' : 'Aggiungi voce'}
                </Button>
              </div>
            </div>
          )}

          {addMode === 'servizio' && preventivo.tipo_offerta !== 'a_corpo' && (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Voce libera senza articolo di magazzino (es. consulenza, installazione, trasporto).
              </p>
              <div>
                <label className="text-xs text-muted-foreground">Descrizione *</label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm"
                  rows={2}
                  value={nuovaDescrizione}
                  onChange={(e) => setNuovaDescrizione(e.target.value)}
                  placeholder="es. Consulenza tecnica 8 ore"
                />
              </div>

              {hasPermesso('magazzino.modifica') && (
                <div className="space-y-2 rounded-md border p-3 bg-muted/30">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={salvaCatalogo}
                      onChange={(e) => {
                        setSalvaCatalogo(e.target.checked);
                        setCatalogoSaveError(null);
                        // Deselezionare = abbandonare l'intento "salva a
                        // catalogo": un eventuale articolo gia' creato in un
                        // tentativo precedente non va piu' riusato in automatico.
                        setCatalogoArticoloCreato(null);
                      }}
                      className="rounded border-border"
                    />
                    Salva nel catalogo articoli/servizi (per riusarla senza ridigitarla)
                  </label>

                  {salvaCatalogo && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Codice (SKU) *</label>
                        <Input
                          value={nuovoCodiceCatalogo}
                          onChange={(e) => setNuovoCodiceCatalogo(e.target.value)}
                          maxLength={100}
                          placeholder="es. SRV-CONSULENZA-01"
                          className="text-sm"
                          disabled={!!catalogoArticoloCreato}
                        />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Fornitore preferenziale *</label>
                        {!catalogoFornitoreSelezionato ? (
                          <Combobox
                            value={catalogoFornitoreQuery}
                            onInputChange={setCatalogoFornitoreQuery}
                            onSelect={(opt) => {
                              const f = catalogoFornitoreResults.find((x) => x.id === opt.value);
                              if (!f) return;
                              setCatalogoFornitoreSelezionato(f);
                            }}
                            options={catalogoFornitoreResults.map<ComboboxOption>((f) => ({
                              value: f.id,
                              label: f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '-',
                            }))}
                            loading={catalogoFornitoreLoading}
                            placeholder="Digita nome fornitore..."
                            className="text-sm"
                            disabled={!!catalogoArticoloCreato}
                            onCreateNew={() => setQuickCreateTarget('catalogoFornitore')}
                            createNewLabel="Crea nuovo fornitore..."
                          />
                        ) : (
                          <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                            <span>
                              {catalogoFornitoreSelezionato.ragione_sociale
                                || [catalogoFornitoreSelezionato.nome, catalogoFornitoreSelezionato.cognome].filter(Boolean).join(' ')}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCatalogoFornitoreSelezionato(null)}
                              disabled={!!catalogoArticoloCreato}
                            >
                              Cambia
                            </Button>
                          </div>
                        )}
                      </div>
                      <p className="col-span-2 text-xs text-muted-foreground">
                        Richiesto dall'anagrafica articoli (fornitore preferenziale obbligatorio, DEC-047). Si puo' cambiare in seguito dalla scheda articolo.
                      </p>
                    </div>
                  )}

                  {catalogoArticoloCreato && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Articolo gia' salvato a catalogo (SKU {catalogoArticoloCreato.sku}): riprova per aggiungere solo la riga.
                    </p>
                  )}

                  {catalogoSaveError && (
                    <p className="text-xs text-destructive">{catalogoSaveError}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {(articoloSelezionato || (addMode === 'servizio' && preventivo.tipo_offerta !== 'a_corpo')) && (
            <div className="grid grid-cols-6 gap-3 pt-2 border-t">
              <div>
                <label className="text-xs text-muted-foreground">UM</label>
                <Combobox
                  value={nuovaUM}
                  onInputChange={setNuovaUM}
                  // Si salva il CODICE, non la descrizione: la fattura elettronica
                  // ammette al massimo 10 caratteri (UnitaMisura = String10Type
                  // nell'XSD FatturaPA) e descrizioni come "Chilogrammi" o
                  // "Anno/Utente" ne hanno 11, rendendo la riga non fatturabile.
                  onSelect={(opt) => setNuovaUM(opt.sublabel ?? opt.label)}
                  options={unitaMisuraOpts
                    .filter((u) => {
                      const q = nuovaUM.toLowerCase();
                      return (
                        u.descrizione.toLowerCase().includes(q) ||
                        u.codice.toLowerCase().includes(q)
                      );
                    })
                    .map<ComboboxOption>((u) => ({ value: u.id, label: u.descrizione, sublabel: u.codice }))}
                  placeholder={addMode === 'servizio' ? 'ore, gg, ...' : ''}
                  className="text-sm"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Quantita *</label>
                <Input
                  type="number"
                  step="0.001"
                  value={nuovaQuantita}
                  onChange={(e) => setNuovaQuantita(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Prezzo unitario</label>
                <Input
                  type="number"
                  step="0.01"
                  value={nuovoPrezzo}
                  onChange={(e) => setNuovoPrezzo(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">IVA %</label>
                <Input
                  type="number"
                  step="0.01"
                  value={nuovaIva}
                  onChange={(e) => setNuovaIva(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Sconto %</label>
                <Input
                  type="number"
                  step="0.01"
                  value={nuovoSconto}
                  onChange={(e) => setNuovoSconto(e.target.value)}
                  className="text-sm"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">
                  Costo unitario{nuovoCostoOrigine === 'catalogo' ? ' (listino)' : ''}
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={nuovoCosto}
                  onChange={(e) => { setNuovoCosto(e.target.value); setNuovoCostoOrigine('manuale'); setNuovoCatalogoFornitoreId(null); }}
                  className="text-sm"
                  placeholder="0"
                />
              </div>
              <div className="col-span-6 flex items-center justify-end gap-3 text-sm border-t border-border pt-2">
                <span className="text-muted-foreground">Importo riga:</span>
                <span className="font-semibold tabular-nums">
                  {nuovaRigaPreviewValida
                    ? currency(nuovaRigaImportoPreview.toFixed(2))
                    : <span className="text-muted-foreground italic font-normal">-</span>}
                </span>
                {nuovaRigaPreviewValida && nuovaIva.trim() !== '' && Number.isFinite(parseFloat(nuovaIva)) && (
                  <span className="text-xs text-muted-foreground">
                    + IVA {nuovaIva}% = {currency((nuovaRigaImportoPreview * (1 + parseFloat(nuovaIva) / 100)).toFixed(2))}
                  </span>
                )}
              </div>
              <div className="col-span-6 flex justify-end gap-2">
                <Button variant="ghost" onClick={resetAddForm}>Annulla</Button>
                <Button
                  onClick={handleAddRiga}
                  disabled={
                    actionLoading
                    || !nuovaDescrizione.trim()
                    || !nuovaQuantita
                    || parseFloat(nuovaQuantita) <= 0
                    || (addMode === 'servizio' && !articoloSelezionato && salvaCatalogo
                      && (!nuovoCodiceCatalogo.trim() || !catalogoFornitoreSelezionato))
                  }
                >
                  {actionLoading ? 'Aggiungo...' : 'Aggiungi riga'}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Righe */}
      <Card className="overflow-hidden">
        <div className="px-4 py-2 border-b border-border bg-muted/50 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-foreground">Righe preventivo</h2>
          {isEditable && preventivo.tipo_offerta !== 'a_corpo' && preventivo.righe.length > 1 && (
            <Button variant="outline" size="sm" onClick={ordinaPerCodice} className="text-xs">
              Ordina per codice
            </Button>
          )}
        </div>
        {preventivo.righe.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            Nessuna riga. Usa "Aggiungi articoli" per selezionare dal catalogo {preventivo.tipo === 'vendita' ? 'aziendale o creare un servizio libero' : 'fornitore'}.
          </div>
        ) : preventivo.tipo_offerta === 'a_corpo' ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-muted-foreground font-medium w-12">#</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Voce di servizio</th>
                {isEditable && <th className="px-4 py-2 w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {preventivo.righe.map((r, idx) => (
                <tr key={r.id} className="border-b border-border align-top">
                  <td className="px-4 py-3 font-mono text-sm font-semibold text-muted-foreground">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{r.descrizione}</div>
                    {r.note && (
                      <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap">{r.note}</div>
                    )}
                  </td>
                  {isEditable && (
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteRiga(r.id)}
                        className="text-destructive text-xs"
                      >
                        Elimina
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            {preventivo.importo_a_corpo && (
              <tfoot>
                <tr className="border-t-2 border-primary">
                  <td colSpan={isEditable ? 2 : 1} className="px-4 py-3 text-right font-semibold">
                    Totale a corpo
                  </td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">
                    {currency(preventivo.importo_a_corpo)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        ) : (
          <>
          <datalist id="sezioni-preventivo-datalist">
            {sezioniUsate.map((s) => <option key={s} value={s} />)}
          </datalist>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {isEditable && <th className="px-4 py-2 w-8"></th>}
                {isEditable && <th className="px-4 py-2 w-10"></th>}
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Tipo</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Codice</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium">Descrizione</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Q.ta</th>
                <th className="text-left px-4 py-2 text-muted-foreground font-medium w-20">UM</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Prezzo</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">IVA %</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Sc. %</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Costo</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Margine</th>
                <th className="text-right px-4 py-2 text-muted-foreground font-medium">Importo</th>
                {isEditable && <th className="px-4 py-2 w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {righeLive.map(({ r, effPrezzo, importoRiga, margine, sottoSoglia }, idx) => {
                const hasPrice = parseFloat(effPrezzo || '0') > 0;
                // Tipo riga dal campo persistito (migration 0018): snapshot dal
                // flag articolo per le righe da catalogo, valore scelto per le
                // libere. Non piu' indovinato lato client.
                const isServizio = r.is_servizio;
                const renderEditable = (campo: 'quantita' | 'prezzo_unitario' | 'iva_perc' | 'sconto_perc' | 'costo_unitario' | 'descrizione' | 'unita_misura' | 'codice_articolo', display: string, suffix: string = '', step: string = '0.01') => {
                  const isEditing = editingField?.rigaId === r.id && editingField?.campo === campo;
                  const isDescrizione = campo === 'descrizione';
                  const isUM = campo === 'unita_misura';
                  const isCodice = campo === 'codice_articolo';
                  const isTesto = isDescrizione || isUM || isCodice;
                  const currentVal = r[campo] ?? '';
                  if (isEditing && isUM) {
                    // Picker UM compatto da lookup (Task F): resta testo libero,
                    // il valore corrente della riga (anche se non nel lookup, es.
                    // storico pre-lookup) e' sempre visibile/modificabile nel campo.
                    return (
                      <div
                        className="flex items-center gap-1"
                        onKeyDown={(e) => {
                          // Il Combobox gestisce internamente Frecce/Enter-su-highlight
                          // (preventDefault quando seleziona un suggerimento) ed Escape
                          // solo per il proprio dropdown, senza notificare il genitore.
                          // Qui intercettiamo in bubbling per allineare il comportamento
                          // agli altri campi editabili inline:
                          // - Escape annulla sempre la modifica;
                          // - Enter salva il testo digitato, ma SOLO se il Combobox non
                          //   ha gia' gestito un Enter-su-highlight (defaultPrevented),
                          //   per evitare un doppio salvataggio.
                          if (e.key === 'Escape') {
                            setEditingField(null);
                            setEditingValue('');
                          } else if (e.key === 'Enter' && !e.defaultPrevented) {
                            void handleSaveField();
                          }
                        }}
                      >
                        <Combobox
                          value={editingValue}
                          onInputChange={setEditingValue}
                          // Si salva il CODICE (max 10 caratteri): vedi nota nel
                          // picker UM del blocco di aggiunta riga.
                          onSelect={(opt) => void handleSaveField(opt.sublabel ?? opt.label)}
                          options={unitaMisuraOpts
                            .filter((u) => {
                              const q = editingValue.toLowerCase();
                              return (
                                u.descrizione.toLowerCase().includes(q) ||
                                u.codice.toLowerCase().includes(q)
                              );
                            })
                            .map<ComboboxOption>((u) => ({ value: u.id, label: u.descrizione, sublabel: u.codice }))}
                          placeholder="UM"
                          className="w-28 text-sm"
                          autoFocus
                          maxLength={10}
                        />
                        <Button variant="ghost" size="sm" onClick={() => void handleSaveField()} className="text-xs px-1">OK</Button>
                      </div>
                    );
                  }
                  if (isEditing) {
                    return (
                      <div className={`flex items-center gap-1 ${isTesto ? '' : 'justify-end'}`}>
                        <Input
                          type={isTesto ? 'text' : 'number'}
                          step={isTesto ? undefined : step}
                          maxLength={isDescrizione ? 500 : isCodice ? 100 : undefined}
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          className={
                            isDescrizione ? 'w-full min-w-[16rem] text-sm'
                              : isCodice ? 'w-32 text-sm font-mono'
                                : 'w-20 text-right text-sm'
                          }
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveField();
                            if (e.key === 'Escape') { setEditingField(null); setEditingValue(''); }
                          }}
                        />
                        <Button variant="ghost" size="sm" onClick={() => void handleSaveField()} className="text-xs px-1">OK</Button>
                      </div>
                    );
                  }
                  if (isEditable) {
                    return (
                      <button
                        onClick={() => startEdit(r.id, campo, currentVal)}
                        className={`group cursor-pointer inline-flex items-center gap-1 hover:underline ${isTesto ? 'text-left' : ''}`}
                        title="Clicca per modificare"
                      >
                        <span>{display}{suffix}</span>
                        <Pencil
                          size={12}
                          className="shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
                        />
                      </button>
                    );
                  }
                  return <>{display}{suffix}</>;
                };
                const puoSalvareACatalogo = r.articolo_id === null && isEditable && hasPermesso('magazzino.modifica');
                const panelAperto = rigaCatalogoPanelId === r.id;
                // Retry di solo collegamento: l'articolo per QUESTA riga e' gia'
                // stato creato a catalogo (codice invariato) e va solo linkato via
                // PUT. Il fornitore preferenziale non serve in questo passaggio
                // (era gia' servito per la POST, non ripetuta): non richiederlo di
                // nuovo, altrimenti dopo un Annulla (che azzera la selezione ma
                // NON la mappa per-riga) il campo fornitore disabilitato
                // bloccherebbe il retry per sempre (nessun modo di riselezionarlo).
                const rigaCatalogoResumeLinkOnly =
                  panelAperto && !!rigaCatalogoArticoloCreato[r.id] && rigaCatalogoArticoloCreato[r.id].sku === rigaCatalogoCodice.trim();
                // Riga di gruppo/sezione (solo visualizzazione): NON draggabile,
                // NON entra nella mappatura idx->preventivo.righe usata dal drag &
                // drop (quello resta sulla <tr> dati sotto, stesso idx di sempre).
                const gruppoInfo = hasSezioni ? gruppoSezioneById.get(r.id) : undefined;
                return (
                  <React.Fragment key={r.id}>
                  {gruppoInfo?.isInizioGruppo && (
                    <tr className="bg-primary text-primary-foreground">
                      <td colSpan={isEditable ? 14 : 11} className="px-4 py-2 font-bold uppercase tracking-wide text-xs">
                        {isEditable && editingGruppoId === r.id ? (
                          <div className="flex items-center gap-1 normal-case font-normal">
                            <input
                              type="text"
                              value={editingGruppoValue}
                              onChange={(e) => setEditingGruppoValue(e.target.value)}
                              maxLength={120}
                              placeholder="Sezione"
                              className="w-64 text-xs rounded border border-border px-1 py-0.5 bg-background text-foreground"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameGruppo(gruppoRigheIdsByInizioId.get(r.id) || [], editingGruppoValue);
                                if (e.key === 'Escape') { setEditingGruppoId(null); setEditingGruppoValue(''); }
                              }}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRenameGruppo(gruppoRigheIdsByInizioId.get(r.id) || [], editingGruppoValue)}
                              className="text-xs px-1 text-primary-foreground hover:text-primary-foreground"
                            >
                              OK
                            </Button>
                          </div>
                        ) : isEditable ? (
                          <button
                            onClick={() => { setEditingGruppoId(r.id); setEditingGruppoValue(gruppoInfo.sezione); }}
                            className="group cursor-pointer inline-flex items-center gap-1.5 hover:underline"
                            title="Clicca per rinominare la sezione (aggiorna tutte le righe del gruppo)"
                          >
                            <span>{gruppoInfo.sezione}</span>
                            <Pencil size={12} className="shrink-0 opacity-70 group-hover:opacity-100" />
                          </button>
                        ) : (
                          gruppoInfo.sezione
                        )}
                      </td>
                    </tr>
                  )}
                  <tr
                    className={`border-b border-border ${!r.confermata ? 'opacity-50' : ''} ${dragIdx === idx ? 'opacity-40' : ''}`}
                    draggable={isEditable}
                    onDragStart={() => setDragIdx(idx)}
                    onDragOver={(e) => { if (isEditable) e.preventDefault(); }}
                    onDrop={() => {
                      if (!isEditable || dragIdx === null || dragIdx === idx) return;
                      const ids = preventivo.righe.map((rr) => rr.id);
                      const [mosso] = ids.splice(dragIdx, 1);
                      ids.splice(idx, 0, mosso);
                      setDragIdx(null);
                      salvaOrdine(ids);
                    }}
                    onDragEnd={() => setDragIdx(null)}
                  >
                    {isEditable && (
                      <td className="px-4 py-2 text-muted-foreground cursor-grab active:cursor-grabbing" title="Trascina per riordinare">
                        <GripVertical size={14} />
                      </td>
                    )}
                    {isEditable && (
                      <td className="px-4 py-2">
                        {/* Checkbox conferma solo sulle righe da validare (es.
                            importate da documento): le righe gia' confermate non
                            lo mostrano (nascosto -> niente rumore). Per escludere
                            una riga confermata dal totale la si elimina. */}
                        {!r.confermata && (
                          <input
                            type="checkbox"
                            checked={r.confermata}
                            onChange={() => handleToggleRiga(r.id, r.confermata)}
                            className="rounded border-border"
                            title="Conferma la riga (entra nei totali e nell'ordine)"
                          />
                        )}
                      </td>
                    )}
                    <td className="px-4 py-2">
                      {isEditable && r.articolo_id === null ? (
                        // Riga libera: tipo editabile. Le righe da catalogo
                        // mostrano il badge read-only (tipo dall'articolo).
                        <select
                          value={isServizio ? 'servizio' : 'prodotto'}
                          onChange={(e) => handleSaveIsServizio(r.id, e.target.value === 'servizio')}
                          className="text-xs rounded border border-border bg-background px-1 py-0.5"
                        >
                          <option value="prodotto">Prodotto</option>
                          <option value="servizio">Servizio</option>
                        </select>
                      ) : isServizio ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">Servizio</span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">Prodotto</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {renderEditable('codice_articolo', r.codice_articolo || '-')}
                    </td>
                    <td className="px-4 py-2">
                      {renderEditable('descrizione', r.descrizione)}
                      <div className="mt-1">
                        {isEditable ? (
                          editingSezioneRigaId === r.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                list="sezioni-preventivo-datalist"
                                type="text"
                                value={editingSezioneValue}
                                onChange={(e) => setEditingSezioneValue(e.target.value)}
                                maxLength={120}
                                placeholder="Sezione"
                                className="w-40 text-xs rounded border border-border px-1 py-0.5 bg-background"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveSezione(r.id, editingSezioneValue);
                                  if (e.key === 'Escape') { setEditingSezioneRigaId(null); setEditingSezioneValue(''); }
                                }}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSaveSezione(r.id, editingSezioneValue)}
                                className="text-xs px-1"
                              >
                                OK
                              </Button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingSezioneRigaId(r.id); setEditingSezioneValue(r.sezione || ''); }}
                              className="group cursor-pointer inline-flex items-center gap-1 hover:underline text-xs text-muted-foreground"
                              title="Clicca per modificare la sezione (usata per i subtotali nel PDF)"
                            >
                              <span>{r.sezione ? `Sezione: ${r.sezione}` : 'Aggiungi sezione'}</span>
                              <Pencil size={11} className="shrink-0 opacity-60 group-hover:opacity-100" />
                            </button>
                          )
                        ) : (
                          r.sezione && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-muted text-muted-foreground">
                              {r.sezione}
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {renderEditable('quantita', qty(r.quantita), '', '0.001')}
                    </td>
                    <td className="px-4 py-2 text-left">
                      {r.unita_misura
                        ? renderEditable('unita_misura', r.unita_misura)
                        : isEditable
                          ? renderEditable('unita_misura', 'UM')
                          : null}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {hasPrice
                        ? renderEditable('prezzo_unitario', currency(r.prezzo_unitario))
                        : isEditable
                          ? renderEditable('prezzo_unitario', 'da inserire')
                          : <span className="text-muted-foreground italic">-</span>}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {renderEditable('iva_perc', r.iva_perc ?? '-', r.iva_perc ? ' %' : '')}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {renderEditable('sconto_perc', r.sconto_perc ?? '-', r.sconto_perc ? ' %' : '')}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {r.costo_unitario != null
                        ? (
                          <span className="inline-flex items-center gap-1 justify-end">
                            {renderEditable('costo_unitario', currency(r.costo_unitario))}
                            {r.scostamento_costo != null && parseFloat(r.scostamento_costo) !== 0 && (
                              <span
                                className={`text-xs ${parseFloat(r.scostamento_costo) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
                                title={`Listino acquisto aggiornato: scostamento ${currency(r.scostamento_costo)} rispetto allo snapshot`}
                              >
                                {parseFloat(r.scostamento_costo) > 0 ? '(+)' : '(-)'}
                              </span>
                            )}
                          </span>
                        )
                        : (!isServizio && isEditable)
                          ? renderEditable('costo_unitario', 'da inserire')
                          : <span className="text-muted-foreground italic">-</span>}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${sottoSoglia ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                      {margine.marginePerc != null ? (
                        <span title={sottoSoglia ? 'Margine sotto la soglia minima' : ''}>
                          {margine.marginePerc.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-muted-foreground italic">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {hasPrice ? currency(importoRiga.toFixed(2)) : '-'}
                    </td>
                    {isEditable && (
                      <td className="px-4 py-2 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {puoSalvareACatalogo && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => (panelAperto ? resetRigaCatalogoPanel() : handleApriRigaCatalogo(r))}
                              className="text-xs px-1"
                              title="Salva a catalogo"
                              // Disabilitato su TUTTE le righe mentre un salvataggio e' in
                              // volo (non solo quella del pannello aperto): evita di aprire
                              // il pannello di un'altra riga mentre la richiesta per questa
                              // e' ancora in corso (Finding 1, review 94af5f7).
                              disabled={rigaCatalogoActionLoading}
                            >
                              <BookmarkPlus size={14} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRiga(r.id)}
                            className="text-destructive text-xs"
                          >
                            Elimina
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {panelAperto && (
                    <tr className="border-b border-border bg-muted/30">
                      <td colSpan={14} className="px-4 py-3">
                        <div className="space-y-2 rounded-md border p-3 bg-background">
                          <p className="text-xs text-muted-foreground">
                            Salva "{r.descrizione}" nel catalogo articoli/servizi (per riusarla senza ridigitarla in altri preventivi).
                          </p>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="text-xs text-muted-foreground">Codice (SKU) *</label>
                              <Input
                                value={rigaCatalogoCodice}
                                onChange={(e) => setRigaCatalogoCodice(e.target.value)}
                                maxLength={100}
                                placeholder="es. ABB-SUPREMO"
                                className="text-sm"
                                disabled={!!rigaCatalogoArticoloCreato[r.id] || rigaCatalogoActionLoading}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">Fornitore preferenziale *</label>
                              {!rigaCatalogoFornitoreSelezionato ? (
                                <Combobox
                                  value={rigaCatalogoFornitoreQuery}
                                  onInputChange={setRigaCatalogoFornitoreQuery}
                                  onSelect={(opt) => {
                                    const f = rigaCatalogoFornitoreResults.find((x) => x.id === opt.value);
                                    if (!f) return;
                                    setRigaCatalogoFornitoreSelezionato(f);
                                  }}
                                  options={rigaCatalogoFornitoreResults.map<ComboboxOption>((f) => ({
                                    value: f.id,
                                    label: f.ragione_sociale || [f.nome, f.cognome].filter(Boolean).join(' ') || '-',
                                  }))}
                                  loading={rigaCatalogoFornitoreLoading}
                                  placeholder="Digita nome fornitore..."
                                  className="text-sm"
                                  disabled={!!rigaCatalogoArticoloCreato[r.id] || rigaCatalogoActionLoading}
                                  onCreateNew={() => setQuickCreateTarget('rigaCatalogoFornitore')}
                                  createNewLabel="Crea nuovo fornitore..."
                                />
                              ) : (
                                <div className="rounded-md border p-2 flex items-center justify-between text-sm">
                                  <span>
                                    {rigaCatalogoFornitoreSelezionato.ragione_sociale
                                      || [rigaCatalogoFornitoreSelezionato.nome, rigaCatalogoFornitoreSelezionato.cognome].filter(Boolean).join(' ')}
                                  </span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setRigaCatalogoFornitoreSelezionato(null)}
                                    disabled={!!rigaCatalogoArticoloCreato[r.id] || rigaCatalogoActionLoading}
                                  >
                                    Cambia
                                  </Button>
                                </div>
                              )}
                            </div>
                            <div className="flex items-end pb-1">
                              <label className="flex items-center gap-2 text-sm cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={rigaCatalogoIsServizio}
                                  onChange={(e) => setRigaCatalogoIsServizio(e.target.checked)}
                                  className="rounded border-border"
                                  disabled={!!rigaCatalogoArticoloCreato[r.id] || rigaCatalogoActionLoading}
                                />
                                E' un servizio
                              </label>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Richiesto dall'anagrafica articoli (fornitore preferenziale obbligatorio, DEC-047). Si puo' cambiare in seguito dalla scheda articolo.
                          </p>
                          {rigaCatalogoArticoloCreato[r.id] && (
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              Articolo gia' salvato a catalogo (SKU {rigaCatalogoArticoloCreato[r.id].sku}): riprova per collegare solo la riga
                              {rigaCatalogoResumeLinkOnly ? ' (fornitore non richiesto in questo passaggio)' : ''}.
                            </p>
                          )}
                          {rigaCatalogoSaveError && (
                            <p className="text-xs text-destructive">{rigaCatalogoSaveError}</p>
                          )}
                          <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={resetRigaCatalogoPanel} disabled={rigaCatalogoActionLoading}>Annulla</Button>
                            <Button
                              onClick={() => handleSalvaRigaACatalogo(r)}
                              disabled={
                                rigaCatalogoActionLoading ||
                                !rigaCatalogoCodice.trim() ||
                                (!rigaCatalogoResumeLinkOnly && !rigaCatalogoFornitoreSelezionato)
                              }
                            >
                              {rigaCatalogoActionLoading ? 'Salvo...' : 'Salva'}
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {gruppoInfo?.isFineGruppo && (
                    <tr className="bg-primary/10 text-primary font-semibold border-t border-primary">
                      <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right">
                        Totale {gruppoInfo.sezione}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">
                        {currency(gruppoInfo.subtotaleGruppo.toFixed(2))}
                      </td>
                      {isEditable && <td></td>}
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {righeLive.some((x) => parseFloat(x.effPrezzo || '0') > 0) && (
              <tfoot>
                <tr className="border-t-2 border-primary">
                  <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right text-muted-foreground">
                    Imponibile (netto sconti)
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {currency(imponibileNetto.toFixed(2))}
                  </td>
                  {isEditable && <td></td>}
                </tr>
                <tr>
                  <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right text-muted-foreground">
                    IVA
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {currency(ivaTotale.toFixed(2))}
                  </td>
                  {isEditable && <td></td>}
                </tr>
                <tr className="border-t border-primary">
                  <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right font-semibold">
                    Totale documento (IVA inclusa)
                  </td>
                  <td className="px-4 py-2 text-right font-semibold tabular-nums">
                    {currency(totaleDocumento.toFixed(2))}
                  </td>
                  {isEditable && <td></td>}
                </tr>
                {margineTotalePerc != null && (
                  <tr className="border-t border-border">
                    <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right text-muted-foreground">
                      Margine totale (netto IVA)
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${righeSottoSogliaCount > 0 ? 'text-red-600 dark:text-red-400 font-medium' : ''}`}>
                      {margineTotalePerc.toFixed(1)}%
                    </td>
                    {isEditable && <td></td>}
                  </tr>
                )}
                {guadagnoNettoTotale != null && (
                  <tr>
                    <td colSpan={isEditable ? 12 : 10} className="px-4 py-2 text-right text-muted-foreground">
                      Guadagno netto (netto IVA)
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      {currency(guadagnoNettoTotale.toFixed(2))}
                    </td>
                    {isEditable && <td></td>}
                  </tr>
                )}
                {righeSottoSogliaCount > 0 && (
                  <tr>
                    <td colSpan={isEditable ? 14 : 11} className="px-4 py-2 text-right text-amber-600 dark:text-amber-400 text-xs">
                      {righeSottoSogliaCount} rig{righeSottoSogliaCount === 1 ? 'a' : 'he'} sotto la soglia di margine
                      {sogliaMargine != null ? ` (${sogliaMargine.toFixed(0)}%)` : ''}.
                    </td>
                  </tr>
                )}
              </tfoot>
            )}
          </table>
          </>
        )}
      </Card>

      <SoggettoQuickCreateDialog
        open={quickCreateTarget !== null}
        onOpenChange={(o) => {
          if (!o) setQuickCreateTarget(null);
        }}
        tipo="fornitore"
        presetNome={
          quickCreateTarget === 'distributore'
            ? distributoriQuery
            : quickCreateTarget === 'catalogoFornitore'
              ? catalogoFornitoreQuery
              : quickCreateTarget === 'rigaCatalogoFornitore'
                ? rigaCatalogoFornitoreQuery
                : ''
        }
        onCreated={handleFornitoreQuickCreato}
      />

      <DuplicaPreventivoDialog
        open={duplicaOpen}
        onOpenChange={setDuplicaOpen}
        preventivoId={preventivo.id}
        tipo={preventivo.tipo}
        token={token}
      />

      <CambiaIntestatarioDialog
        open={cambiaIntestatarioOpen}
        onOpenChange={setCambiaIntestatarioOpen}
        preventivoId={preventivo.id}
        tipo={preventivo.tipo}
        intestatarioAttuale={preventivo.tipo === 'vendita' ? preventivo.cliente_nome : preventivo.fornitore_nome}
        token={token}
        onSuccess={() => void fetchData()}
      />

      {generaOrdineOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => !actionLoading && setGeneraOrdineOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 text-lg font-semibold">Genera ordine di vendita</h2>
            <p className="mb-4 text-sm text-muted-foreground">
              Puoi allegare ora l'ordine firmato dal cliente (opzionale) oppure caricarlo
              in seguito dal dettaglio dell'ordine.
            </p>
            <div className="mb-4">
              <label className="mb-1 block text-sm font-medium">
                Ordine firmato dal cliente (opzionale)
              </label>
              <input
                type="file"
                accept=".pdf,.p7m,.jpg,.jpeg,.png"
                disabled={actionLoading}
                onChange={(e) => setFileFirmato(e.target.files?.[0] ?? null)}
                className="text-sm"
              />
              {fileFirmato && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Selezionato: {fileFirmato.name}
                </p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                Formati ammessi: PDF, P7M (firma digitale), JPG, PNG.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setGeneraOrdineOpen(false)}
                disabled={actionLoading}
              >
                Annulla
              </Button>
              <Button
                onClick={() => void handleGeneraOrdineVendita(fileFirmato)}
                disabled={actionLoading}
              >
                {actionLoading ? 'Generazione...' : 'Genera ordine'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
